import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { scheduleAPI } from '../services/api.ts';
import { usePopup } from '../context/PopupContext.tsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapPicker from '../components/MapPicker.tsx';
import MobileDatePicker from '../components/MobileDatePicker.tsx';
import SignatureCanvas from 'react-signature-canvas';
import ServiceCompletionSheet, { ServiceCompletionDetail } from '../components/ServiceCompletionSheet.tsx';

interface ScheduleItem {
  id: string;
  date: string;
  time: string;
  location: string;
  address: string;
  status?: 'pending' | 'completed' | 'canceled';
  projectName?: string;
  customerName?: string;
  contactNo?: string;
  contactPerson?: string;
  workFlowStatusId?: number;
  startTime?: string | null;
  imageUrl?: string;
  title?: string;
  clockIn?: string | null;
  clockOut?: string | null;
  totalHour?: number;
  remarks?: string | null;
  jobApplicationId?: number | null;
  profileLoginId?: string | null;
  scheduleType?: string;
  isBmSchedule?: boolean;
  assignmentId?: number | null;
  sitePicId?: number | null;
  businessScheduleId?: number | null;
  deliveryOrderId?: number | null;
  serviceOrderId?: number | null;
  scheduleRecordType?: string;
  documentNo?: string;
  isDeliveryCompleted?: boolean;
  isServiceCompleted?: boolean;
  canJobCheckIn?: boolean;
  isJobCheckedIn?: boolean;
  canTripCheckIn?: boolean;
  isTripCheckedIn?: boolean;
  scheduledTime?: string;
  siteName?: string;
  workerDisplayName?: string;
  scheduleDate?: string;
  // legacy
  isTeamService?: boolean;
  canTeamCheckIn?: boolean;
  isTeamCheckedIn?: boolean;
}

interface BmCheckInModalData {
  kind: 'job' | 'trip';
  assignmentId?: number;
  sitePicId?: number;
  businessScheduleId?: number;
  workerDisplayName: string;
  siteName: string;
  image: File | null;
  latitude: number | null;
  longitude: number | null;
}

interface DeliveryCompletionLine {
  id: number;
  name: string;
  description?: string;
  orderedQuantity: number;
  receivedQuantity: number;
  confirmed: boolean;
}

interface DeliveryCompletionPhoto {
  dataUrl: string;
  caption: string;
}

interface DeliveryCompletionDraft {
  deliveryOrderId: number;
  documentNo: string;
  customerName: string;
  deliveryDate?: string;
  checkedInDateTime?: string;
  receivedBy: string;
  notes: string;
  lines: DeliveryCompletionLine[];
  photos: DeliveryCompletionPhoto[];
  latitude: number | null;
  longitude: number | null;
}

interface ServiceJobPhoto {
  id: number;
  type: 'before' | 'after';
  dataUrl: string;
  caption?: string;
}

interface ServicePhotoViewer {
  type: 'before' | 'after';
  index: number;
}

interface ExtraServiceCatalogItem {
  id: number;
  code?: string;
  name: string;
  description?: string;
  price: number;
  taxRate: number;
  measurementTypeId?: number | null;
}

interface ExtraServiceDetail {
  scheduleId: number;
  serviceOrderId: number;
  documentNo: string;
  customerName: string;
  currentSubtotal: number;
  currentTax: number;
  currentTotal: number;
  paidAmount: number;
  balanceAmount: number;
  activeLineCount: number;
  canEdit: boolean;
  editMessage?: string | null;
  items: ExtraServiceCatalogItem[];
}

interface ScheduleActivityItem {
  eventTime: string;
  action: string;
  detail?: string;
  by?: string;
  role?: string;
  isCurrent?: boolean;
}

interface ScheduleActivityLog {
  title: string;
  items: ScheduleActivityItem[];
}

type ScheduleShowFilter =
  | 'all'
  | 'jobs'
  | 'interview'
  | 'new-joiner'
  | 'transport'
  | 'compliance'
  | 'notice'
  | 'company-event'
  | 'holiday'
  | 'leave';

type ScheduleDateFilter = 'all' | 'today' | 'next-7' | 'upcoming' | 'custom';
type ScheduleJobTypeFilter = 'all' | 'delivery' | 'service' | 'site-visit' | 'touch-up';
type ScheduleQuickFilter = 'all' | 'today' | 'scheduled' | 'pending';

interface ScheduleListFilters {
  show: ScheduleShowFilter;
  date: ScheduleDateFilter;
  fromDate: string;
  toDate: string;
  jobType: ScheduleJobTypeFilter;
  worker: string;
}

const EMPTY_LIST_FILTERS: ScheduleListFilters = {
  show: 'all',
  date: 'all',
  fromDate: '',
  toDate: '',
  jobType: 'all',
  worker: '',
};

// Mock data for demonstration
const mockSchedules: ScheduleItem[] = [
  
];

// Add CheckInData interface similar to Dashboard
interface CheckInData {
  scheduleId: string;
  image: File | null;
  latitude: number | null;
  longitude: number | null;
}

// Add this helper function to format WhatsApp numbers
const formatWhatsAppNumber = (phone: string): string => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/[^0-9]/g, '');
  
  // If the number already starts with '6' (Malaysia country code), keep it as is
  // Otherwise, prepend '6' to ensure proper WhatsApp link formatting
  return cleaned.startsWith('6') ? cleaned : `6${cleaned}`;
};

// Safely format a time string to HH:mm
const formatTime = (isoString?: string | null): string | null => {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const formatBmTimeLabel = (timeText?: string | null): string => {
  if (!timeText) return '';
  const trimmed = timeText.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return trimmed.toLowerCase();
  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const suffix = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12 || 12;
  return `${hour}.${minute}${suffix}`;
};

const formatBmScheduleDateTime = (dateStr?: string, timeStr?: string): string => {
  if (!dateStr) return timeStr ? formatBmTimeLabel(timeStr) : '';
  const parts = dateStr.split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((x) => Number.isNaN(x))) {
    return timeStr ? formatBmTimeLabel(timeStr) : dateStr;
  }
  const datePart = `${parts[2]}/${parts[1]}/${parts[0]}`;
  const timePart = formatBmTimeLabel(timeStr);
  return timePart ? `${datePart} ${timePart}` : datePart;
};

const formatLiveClock = (date: Date): string =>
  date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

const formatActivityDateTime = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, 'yyyy-MM-dd · HH:mm');
};

const SITE_VISIT_REPORT_KEY = 'hr_site_visit_reports_v1';
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

interface SiteVisitReportDraft {
  scheduleId: string;
  findings: string;
  equipment: string;
  recommendation: string;
  requirements: string[];
  photos: string[];
  submittedAt?: string;
}

interface LeaveDayInfo {
  kind: 'mc' | 'leave' | 'holiday';
  label: string;
}

interface AttendanceStatus {
  clockIn: string | null;
  clockOut: string | null;
}

const readSiteVisitReports = (): Record<string, SiteVisitReportDraft> => {
  try {
    return JSON.parse(localStorage.getItem(SITE_VISIT_REPORT_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeSiteVisitReport = (draft: SiteVisitReportDraft) => {
  const all = readSiteVisitReports();
  all[draft.scheduleId] = draft;
  localStorage.setItem(SITE_VISIT_REPORT_KEY, JSON.stringify(all));
};

const formatNiceDayLabel = (date: Date, isToday: boolean): string => {
  if (isToday) return 'Today';
  return `${WEEKDAY_SHORT[date.getDay()]} ${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)}`;
};

const parseApiDateOnly = (value: unknown): Date | null => {
  if (value == null || value === '') return null;
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isLeaveOnlyRow = (schedule: ScheduleItem): boolean => {
  if (schedule.isBmSchedule || schedule.jobApplicationId) return false;
  const title = (schedule.title || '').trim();
  if (!title) return false;
  return /leave|medical|\bmc\b|maternity|home leave|unpaid/i.test(title) && !schedule.address;
};

const resolveLeaveKind = (label: string): LeaveDayInfo['kind'] =>
  isSickLeaveTitle(label) ? 'mc' : 'leave';

const isSiteVisitSchedule = (schedule: ScheduleItem): boolean => {
  const blob = `${schedule.title || ''} ${schedule.remarks || ''} ${schedule.siteName || ''}`.toLowerCase();
  if (blob.includes('site visit')) return true;
  if (schedule.sitePicId && !schedule.assignmentId) return true;
  return false;
};

const isSickLeaveTitle = (title?: string | null): boolean =>
  !!title && /sick|medical|\bmc\b/i.test(title);

const isHolidayTitle = (title?: string | null): boolean =>
  !!title && /holiday|public holiday|ph\b/i.test(title);

const getScheduleCardIcon = (schedule: ScheduleItem): string => {
  if (isSiteVisitSchedule(schedule)) return '📍';
  if (schedule.isBmSchedule) return '🚚';
  if (schedule.workFlowStatusId === 2) return '🔧';
  return '📋';
};

const Schedule: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = usePopup();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true); // Flag to track if more data is available
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Separate loading state for pagination
  const pageSize = 25;
  
  // Add new state variables for the image upload modal
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showBmCheckInModal, setShowBmCheckInModal] = useState(false);
  const [bmCheckInData, setBmCheckInData] = useState<BmCheckInModalData | null>(null);
  const [bmModalClock, setBmModalClock] = useState(new Date());
  const [checkInData, setCheckInData] = useState<CheckInData>({
    scheduleId: '',
    image: null,
    latitude: null,
    longitude: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [printingReportId, setPrintingReportId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bmFileInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [leaveDayMap, setLeaveDayMap] = useState<Record<string, LeaveDayInfo>>({});
  const [eventDayCounts, setEventDayCounts] = useState<Record<string, number>>({});
  const [holidayDays, setHolidayDays] = useState<Set<string>>(new Set());
  const [todayAttendance, setTodayAttendance] = useState<AttendanceStatus | null>(null);
  const [scheduleNow, setScheduleNow] = useState(new Date());
  const [siteVisitReports, setSiteVisitReports] = useState<Record<string, SiteVisitReportDraft>>(() => readSiteVisitReports());
  const [showSiteVisitReport, setShowSiteVisitReport] = useState(false);
  const [siteVisitReportSchedule, setSiteVisitReportSchedule] = useState<ScheduleItem | null>(null);
  const [siteVisitDraft, setSiteVisitDraft] = useState<SiteVisitReportDraft>({
    scheduleId: '',
    findings: '',
    equipment: '',
    recommendation: '',
    requirements: [''],
    photos: [],
  });
  const siteVisitPhotoInputRef = useRef<HTMLInputElement>(null);
  const [showDeliveryCompletion, setShowDeliveryCompletion] = useState(false);
  const [deliveryCompletionStage, setDeliveryCompletionStage] = useState<'edit' | 'confirm'>('edit');
  const [deliveryCompletionDraft, setDeliveryCompletionDraft] = useState<DeliveryCompletionDraft | null>(null);
  const [deliveryCompletionLoading, setDeliveryCompletionLoading] = useState(false);
  const [deliveryCompletionSubmitting, setDeliveryCompletionSubmitting] = useState(false);
  const deliveryPhotoInputRef = useRef<HTMLInputElement>(null);
  const deliverySignatureRef = useRef<SignatureCanvas | null>(null);
  const [jobActionSchedule, setJobActionSchedule] = useState<ScheduleItem | null>(null);
  const [scheduleActivity, setScheduleActivity] = useState<ScheduleActivityLog | null>(null);
  const [scheduleActivityLoading, setScheduleActivityLoading] = useState(false);
  const [serviceCompletionDetail, setServiceCompletionDetail] = useState<ServiceCompletionDetail | null>(null);
  const [serviceCompletionLoading, setServiceCompletionLoading] = useState(false);
  const [jobPhotoSchedule, setJobPhotoSchedule] = useState<ScheduleItem | null>(null);
  const [serviceJobPhotos, setServiceJobPhotos] = useState<ServiceJobPhoto[]>([]);
  const [servicePhotoViewer, setServicePhotoViewer] = useState<ServicePhotoViewer | null>(null);
  const [servicePhotoLoading, setServicePhotoLoading] = useState(false);
  const [servicePhotoUploading, setServicePhotoUploading] = useState(false);
  const beforePhotoInputRef = useRef<HTMLInputElement>(null);
  const afterPhotoInputRef = useRef<HTMLInputElement>(null);
  const servicePhotoSwipeStartX = useRef<number | null>(null);
  const [otSchedule, setOtSchedule] = useState<ScheduleItem | null>(null);
  const [otHours, setOtHours] = useState('1');
  const [otReason, setOtReason] = useState('');
  const [otSubmitting, setOtSubmitting] = useState(false);
  const [extraServiceSchedule, setExtraServiceSchedule] = useState<ScheduleItem | null>(null);
  const [extraServiceDetail, setExtraServiceDetail] = useState<ExtraServiceDetail | null>(null);
  const [extraServiceLoading, setExtraServiceLoading] = useState(false);
  const [extraServiceSubmitting, setExtraServiceSubmitting] = useState(false);
  const [extraServiceSearch, setExtraServiceSearch] = useState('');
  const [extraServiceSelections, setExtraServiceSelections] = useState<Record<number, string>>({});
  const [listSearch, setListSearch] = useState('');
  const [listQuickFilter, setListQuickFilter] = useState<ScheduleQuickFilter>('all');
  const [showListFilter, setShowListFilter] = useState(false);
  const [listFilters, setListFilters] = useState<ScheduleListFilters>({ ...EMPTY_LIST_FILTERS });
  const [draftListFilters, setDraftListFilters] = useState<ScheduleListFilters>({ ...EMPTY_LIST_FILTERS });
  const [filterDatePickerTarget, setFilterDatePickerTarget] = useState<'from' | 'to' | null>(null);
  
  // Add the MapPicker state variables
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  
  // Format date for API calls
  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initial data load and pagination reset
  useEffect(() => {
    setPageNumber(1);
    setHasMore(true);
    setSchedules([]);
  }, [
    selectedDate,
    currentMonth,
    viewMode,
    listFilters.date,
    listFilters.fromDate,
    listFilters.toDate,
  ]);

  // Fetch schedules when pageNumber changes
  useEffect(() => {
    fetchSchedules(pageNumber > 1, pageNumber);
  }, [
    pageNumber,
    selectedDate,
    currentMonth,
    viewMode,
    listFilters.date,
    listFilters.fromDate,
    listFilters.toDate,
  ]);

  // Set up scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (!loading && !isLoadingMore && hasMore) {
        const scrollBottom = window.scrollY + window.innerHeight;
        const pageHeight = document.documentElement.scrollHeight;
        if (scrollBottom >= pageHeight - 100) loadMore();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, isLoadingMore, hasMore]);

  // Load more data by incrementing page number
  const loadMore = () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    setPageNumber(prevPage => prevPage + 1);
  };
  
  // Fetch schedules from API
  const fetchSchedules = async (isLoadingMore = false, customPageNumber = 1) => {
    if (!isLoadingMore) {
      setLoading(true);
    }
    try {
      let startDate: string;
      let endDate: string;
      const activeDate = selectedDate ?? new Date();
      if (viewMode === 'list') {
        const today = new Date();
        if (listFilters.date === 'today') {
          startDate = formatDateForApi(today);
          endDate = startDate;
        } else if (listFilters.date === 'next-7') {
          const lastDay = new Date(today);
          lastDay.setDate(lastDay.getDate() + 6);
          startDate = formatDateForApi(today);
          endDate = formatDateForApi(lastDay);
        } else if (listFilters.date === 'upcoming') {
          const lastDay = new Date(today);
          lastDay.setDate(lastDay.getDate() + 62);
          startDate = formatDateForApi(today);
          endDate = formatDateForApi(lastDay);
        } else if (listFilters.date === 'custom' && (listFilters.fromDate || listFilters.toDate)) {
          startDate = listFilters.fromDate || listFilters.toDate;
          endDate = listFilters.toDate || listFilters.fromDate;
        } else {
          startDate = formatDateForApi(startOfMonth(currentMonth));
          endDate = formatDateForApi(endOfMonth(currentMonth));
        }
      } else {
        startDate = formatDateForApi(activeDate);
        endDate = startDate;
      }
      console.log('Fetching schedules with date range:', { startDate, endDate });
      const response = await scheduleAPI.getUpcomingServices({
        PageNumber: customPageNumber,
        PageSize: pageSize,
        StartDate: startDate,
        EndDate: endDate
      });
      const newSchedules = (response.data || []).map((item: any, idx: number) => {
        const clockInRaw = item.clockIn ?? item.ClockIn ?? null;
        const clockOutRaw = item.clockOut ?? item.ClockOut ?? null;
        const clockInDate = clockInRaw ? new Date(clockInRaw as string) : null;
        const validClock = clockInDate && !isNaN(clockInDate.getTime()) ? clockInDate : null;
        const jaId = item.jobApplicationId ?? item.JobApplicationId;
        const profileLogin = item.profileLoginId ?? item.ProfileLoginId ?? null;
        const pad2 = (n: number) => String(n).padStart(2, '0');
        const localYmd = validClock
          ? `${validClock.getFullYear()}-${pad2(validClock.getMonth() + 1)}-${pad2(validClock.getDate())}`
          : '';
        const localHm = validClock
          ? validClock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
          : '';
        const scheduleType = (item.scheduleType ?? item.ScheduleType ?? '').toString();
        const isBmSchedule = scheduleType === 'bm-schedule' || scheduleType === 'team-service' || scheduleType === 'bm-delivery';
        const assignmentId = item.assignmentId ?? item.AssignmentId ?? null;
        const sitePicId = item.sitePicId ?? item.SitePicId ?? null;
        const businessScheduleId = item.businessScheduleId ?? item.BusinessScheduleId ?? null;
        const deliveryOrderId = item.deliveryOrderId ?? item.DeliveryOrderId ?? null;
        const serviceOrderId = item.serviceOrderId ?? item.ServiceOrderId ?? null;
        const scheduleRecordType = String(item.scheduleRecordType ?? item.ScheduleRecordType ?? '');
        const isDeliveryCompleted = !!(item.isDeliveryCompleted ?? item.IsDeliveryCompleted);
        const isServiceCompleted = !!(item.isServiceCompleted ?? item.IsServiceCompleted);
        const isJobCheckedIn = !!(item.isJobCheckedIn ?? item.IsJobCheckedIn ?? item.isTeamCheckedIn ?? item.IsTeamCheckedIn);
        const isTripCheckedIn = !!(item.isTripCheckedIn ?? item.IsTripCheckedIn);
        const scheduledTime = (item.scheduledTime ?? item.ScheduledTime ?? '').toString();
        const canJobCheckIn = item.canJobCheckIn ?? item.CanJobCheckIn ?? item.canTeamCheckIn ?? item.CanTeamCheckIn ?? (isBmSchedule && !!assignmentId && !isJobCheckedIn);
        const canTripCheckIn = item.canTripCheckIn ?? item.CanTripCheckIn ?? (isBmSchedule && !!sitePicId && !isTripCheckedIn);
        const scheduleDateRaw = item.scheduleDate ?? item.ScheduleDate ?? null;
        const scheduleDate = scheduleDateRaw
          ? String(scheduleDateRaw).substring(0, 10)
          : (isBmSchedule ? startDate : '');
        const rowDate = validClock ? localYmd : (scheduleDate || item.date || item.Date || '');
        const rowTime = validClock ? localHm : (scheduledTime || item.time || item.Time || '');
        const pendingActions = (canJobCheckIn && !!assignmentId) || (canTripCheckIn && !!sitePicId);
        return {
          ...item,
          clockIn: clockInRaw,
          clockOut: clockOutRaw,
          id:
            item.id ??
            item.Id ??
            (isBmSchedule && businessScheduleId
              ? `bm-${businessScheduleId}-${assignmentId || 0}-${sitePicId || 0}`
              : deliveryOrderId
              ? `do-${deliveryOrderId}`
              : jaId != null && clockInRaw
              ? `ja-${jaId}-${clockInRaw}`
              : `row-${idx}-${validClock?.getTime() ?? idx}`),
          date: rowDate,
          time: rowTime,
          remarks: item.remarks || item.Remarks || null,
          address: item.address || item.Address || null,
          title: item.title || item.Title || null,
          siteName: item.siteName ?? item.SiteName ?? item.title ?? item.Title ?? null,
          contactPerson: item.contactPerson ?? item.ContactPerson ?? null,
          contactNo: (item.contactNo ?? item.ContactNo ?? '').toString().trim() || undefined,
          workerDisplayName: item.workerDisplayName ?? item.WorkerDisplayName ?? null,
          jobApplicationId: jaId ?? null,
          profileLoginId: profileLogin && String(profileLogin).trim() ? String(profileLogin).trim() : null,
          scheduleType,
          isBmSchedule,
          assignmentId,
          sitePicId,
          businessScheduleId,
          deliveryOrderId,
          serviceOrderId,
          scheduleRecordType,
          documentNo: item.documentNo ?? item.DocumentNo ?? null,
          isDeliveryCompleted,
          isServiceCompleted,
          canJobCheckIn: !!canJobCheckIn,
          isJobCheckedIn,
          canTripCheckIn: !!canTripCheckIn,
          isTripCheckedIn,
          scheduledTime,
          scheduleDate: scheduleDate || rowDate,
          isTeamService: isBmSchedule,
          canTeamCheckIn: !!canJobCheckIn,
          isTeamCheckedIn: isJobCheckedIn,
          workFlowStatusId: isBmSchedule
            ? (scheduleRecordType === 'delivery'
              ? (isDeliveryCompleted ? 2 : 1)
              : (pendingActions ? 0 : (isJobCheckedIn || isTripCheckedIn ? 1 : 0)))
            : item.workFlowStatusId
        };
      });
      if (isLoadingMore) {
        setSchedules(prev => [...prev, ...newSchedules]);
      } else {
        setSchedules(newSchedules);
      }
      if (newSchedules.length < pageSize) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      showError('Failed to load schedules. Please try again.');
      if (isLoadingMore) {
        const start = (customPageNumber - 1) * pageSize;
        const end = customPageNumber * pageSize;
        const moreMockData = mockSchedules.slice(start, end);
        if (moreMockData.length > 0) {
          setSchedules(prev => [...prev, ...moreMockData]);
        }
      } else {
        setSchedules([]);
      }
    } finally {
      if (!isLoadingMore) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const pickDefaultDateForMonth = (monthDate: Date): Date => {
    const today = new Date();
    if (
      today.getFullYear() === monthDate.getFullYear() &&
      today.getMonth() === monthDate.getMonth()
    ) {
      return today;
    }
    return new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  };

  // Previous and next month handlers
  const prevMonth = () => {
    const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(nextMonthDate);
    setSelectedDate(pickDefaultDateForMonth(nextMonthDate));
  };

  const nextMonth = () => {
    const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(nextMonthDate);
    setSelectedDate(pickDefaultDateForMonth(nextMonthDate));
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    if (
      date.getMonth() !== currentMonth.getMonth() ||
      date.getFullYear() !== currentMonth.getFullYear()
    ) {
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  };

  // Get days in current month
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Calendar event counts
  useEffect(() => {
    const startStr = formatDateForApi(monthStart);
    const endStr = formatDateForApi(monthEnd);
    scheduleAPI.getEventDays({ StartDate: startStr, EndDate: endStr })
      .then((rows: any[]) => {
        const counts: Record<string, number> = {};
        const holidays = new Set<string>();
        (rows || []).forEach((r: any) => {
          const d = r.date || r.Date || r.createdDate || r.CreatedDateTime;
          if (!d) return;
          const iso = String(d).substring(0, 10);
          const count = Number(r.count ?? r.Count ?? 1);
          counts[iso] = Number.isFinite(count) && count > 0 ? count : 1;
          if (r.isPublicHoliday ?? r.IsPublicHoliday) holidays.add(iso);
        });
        setEventDayCounts(counts);
        setHolidayDays(holidays);
      })
      .catch(() => {
        setEventDayCounts({});
        setHolidayDays(new Set());
      });
  }, [currentMonth]);

  useEffect(() => {
    scheduleAPI.getCheckInHistory(1, 1)
      .then((response: any) => {
        const latest = response?.data?.[0] ?? response?.Data?.[0];
        const clockIn = latest?.CreatedDateTime ?? latest?.createdDateTime ?? null;
        const clockOut = latest?.CheckOutDateTime ?? latest?.checkOutDateTime ?? null;
        const clockInDate = clockIn ? new Date(clockIn) : null;
        const today = new Date();
        const isToday =
          clockInDate &&
          clockInDate.getFullYear() === today.getFullYear() &&
          clockInDate.getMonth() === today.getMonth() &&
          clockInDate.getDate() === today.getDate();

        setTodayAttendance(isToday ? { clockIn, clockOut } : null);
      })
      .catch(() => setTodayAttendance(null));
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setScheduleNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    scheduleAPI.getLeaveHistory()
      .then((res: any) => {
        const rows = [
          ...(Array.isArray(res?.approved) ? res.approved : []),
          ...(Array.isArray(res?.Approved) ? res.Approved : []),
        ];
        const map: Record<string, LeaveDayInfo> = {};
        const monthStartStr = formatDateForApi(monthStart);
        const monthEndStr = formatDateForApi(monthEnd);

        rows.forEach((row: any) => {
          const wf = row.WorkFlowStatusId ?? row.workFlowStatusId;
          const status = row.Status || row.status || '';
          if (wf !== 2 && !/approved/i.test(String(status))) return;

          const startRaw = row.StartDate ?? row.startDate ?? row.ApplyDateTime ?? row.applyDateTime;
          const endRaw = row.EndDate ?? row.endDate ?? row.EndDateTime ?? row.endDateTime ?? startRaw;
          const start = parseApiDateOnly(startRaw);
          const end = parseApiDateOnly(endRaw) ?? start;
          if (!start || !end) return;

          const typeName = String(row.LeaveType || row.leaveType || row.Title || row.title || 'Leave');
          const kind = resolveLeaveKind(typeName);
          const label = kind === 'mc' ? 'On medical leave (MC)' : 'On approved leave';

          for (
            let d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            d.getTime() <= end.getTime();
            d.setDate(d.getDate() + 1)
          ) {
            const iso = formatDateForApi(d);
            if (iso < monthStartStr || iso > monthEndStr) continue;
            map[iso] = { kind, label };
          }
        });
        setLeaveDayMap(map);
      })
      .catch(() => setLeaveDayMap({}));
  }, [currentMonth]);

  // Helper functions to check schedule status
  const isScheduleCompleted = (schedule: ScheduleItem): boolean => {
    return schedule.workFlowStatusId === 2; // 2 = Completed
  };

  const isScheduleInProgress = (schedule: ScheduleItem): boolean => {
    return schedule.workFlowStatusId === 1; // 1 = Progress
  };

  useEffect(() => {
    if (!showBmCheckInModal) return undefined;
    setBmModalClock(new Date());
    const timer = setInterval(() => setBmModalClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, [showBmCheckInModal]);

  const canCheckIn = (schedule: ScheduleItem): boolean => {
    if (schedule.isBmSchedule) {
      return (schedule.canJobCheckIn && !!schedule.assignmentId) ||
        (schedule.canTripCheckIn && !!schedule.sitePicId);
    }
    return schedule.workFlowStatusId === 0 && !schedule.startTime;
  };

  const isBmSchedulePending = (schedule: ScheduleItem): boolean =>
    (schedule.canJobCheckIn && !!schedule.assignmentId) ||
    (schedule.canTripCheckIn && !!schedule.sitePicId);

  const openBmCheckInModal = (schedule: ScheduleItem, kind: 'job' | 'trip') => {
    setBmCheckInData({
      kind,
      assignmentId: schedule.assignmentId ?? undefined,
      sitePicId: schedule.sitePicId ?? undefined,
      businessScheduleId: schedule.businessScheduleId ?? undefined,
      workerDisplayName: schedule.workerDisplayName || 'Staff',
      siteName: schedule.siteName || schedule.title || 'Service site',
      image: null,
      latitude: null,
      longitude: null,
    });
    setShowBmCheckInModal(true);
    getCurrentLocationForBm();
  };

  const getCurrentLocationForBm = () => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBmCheckInData((prev) => prev ? ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }) : prev);
      },
      (error) => {
        console.error('Error getting location:', error);
      }
    );
  };

  const handleBmImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    setBmCheckInData((prev) => prev ? ({ ...prev, image: file }) : prev);
  };

  const handleSubmitBmCheckIn = async () => {
    if (!bmCheckInData) return;
    if (!bmCheckInData.image) {
      toast.error('Please upload an image');
      return;
    }
    if (!bmCheckInData.latitude || !bmCheckInData.longitude) {
      toast.error('Location data is required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (bmCheckInData.kind === 'job') {
        if (!bmCheckInData.assignmentId) {
          showError('Assignment not found for job check-in.');
          return;
        }
        await scheduleAPI.jobServiceCheckIn(bmCheckInData.assignmentId);
      } else {
        if (!bmCheckInData.sitePicId || !bmCheckInData.businessScheduleId) {
          showError('Trip schedule not found for check-in.');
          return;
        }
        await scheduleAPI.tripServiceCheckIn(bmCheckInData.sitePicId, bmCheckInData.businessScheduleId);
      }
      showSuccess(bmCheckInData.kind === 'job' ? 'Job check-in successful.' : 'Trip check-in successful.');
      setShowBmCheckInModal(false);
      setBmCheckInData(null);
      setPageNumber(1);
      setHasMore(true);
      await fetchSchedules(false, 1);
    } catch (error: any) {
      console.error('Error checking in:', error);
      showError(error?.message || 'Failed to check in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update handleCheckIn to show modal instead of direct API call
  const handleCheckIn = async (e: React.MouseEvent, scheduleId: string) => {
    e.preventDefault();
    initiateCheckIn(scheduleId);
  };

  const handleAttendanceClockOut = async (schedule: ScheduleItem) => {
    const checkInId = Number(schedule.id);
    if (!Number.isInteger(checkInId) || checkInId <= 0 || schedule.clockOut) return;

    setIsSubmitting(true);
    try {
      await scheduleAPI.clockOutById(checkInId);
      const clockOut = new Date().toISOString();
      setTodayAttendance((current) => current?.clockIn ? { ...current, clockOut } : current);
      await fetchSchedules(false, 1);
      toast.success('Clocked out successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to clock out');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Add initiateCheckIn function
  const initiateCheckIn = (scheduleId: string) => {
    setCheckInData({
      scheduleId,
      image: null,
      latitude: null,
      longitude: null,
    });
    setShowCheckInModal(true);
    getCurrentLocation(); // Start getting location immediately
  };
  
  // Add getCurrentLocation function
  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCheckInData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };
  
  // Add handleImageChange function
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // Store the raw File object
      const file = e.target.files[0];
      console.log('Selected file:', file.name, file.type, `${Math.round(file.size / 1024)} KB`);
      setCheckInData(prev => ({
        ...prev,
        image: file
      }));
    }
  };
  
  // Add handleSubmitCheckIn function
  const handleSubmitCheckIn = async () => {
    if (!checkInData.image) {
      toast.error('Please upload an image');
      return;
    }

    if (!checkInData.latitude || !checkInData.longitude) {
      toast.error('Location data is required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Starting check-in for schedule:', checkInData.scheduleId);
      
      // Call the API with the file as binary data
      await scheduleAPI.checkIn(
        Number(checkInData.scheduleId), 
        checkInData.image,
        checkInData.latitude.toString(),
        checkInData.longitude.toString()
      );
      
      // Show success toast and close modal
      toast.success('Successfully checked in! Redirecting to details page...');
      setShowCheckInModal(false);
      
      // Wait for 1.5 seconds to show the toast before navigating
      setTimeout(() => {
        // Navigate to schedule detail page
        navigate(`/schedule/${checkInData.scheduleId}`);
      }, 1500);
      
    } catch (error: any) {
      console.error('Error checking in:', error);
      
      let errorMessage = error.message || 'Failed to check in. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle address click
  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    setShowMapPicker(true);
  };

  const todayIso = formatDateForApi(new Date());
  const activeSelectedDate = selectedDate ?? new Date();
  const selectedIso = formatDateForApi(activeSelectedDate);
  const leaveRowForDay = schedules.find((s) => isLeaveOnlyRow(s));
  const selectedLeaveInfo =
    leaveDayMap[selectedIso] ||
    (leaveRowForDay
      ? {
          kind: resolveLeaveKind(leaveRowForDay.title || ''),
          label: resolveLeaveKind(leaveRowForDay.title || '') === 'mc'
            ? 'On medical leave (MC)'
            : 'On approved leave',
        }
      : undefined);
  const selectedHolidayBanner = schedules.find((s) => isHolidayTitle(s.title))?.title;
  const allJobCards = viewMode === 'list'
    ? schedules
    : schedules.filter((s) => !isLeaveOnlyRow(s));

  const scheduleSearchText = (schedule: ScheduleItem): string =>
    [
      schedule.title,
      schedule.documentNo,
      schedule.projectName,
      schedule.customerName,
      schedule.siteName,
      schedule.contactPerson,
      schedule.contactNo,
      schedule.address,
      schedule.remarks,
      schedule.workerDisplayName,
    ].filter(Boolean).join(' ').toLowerCase();

  const scheduleMatchesShowFilter = (schedule: ScheduleItem, filter: ScheduleShowFilter): boolean => {
    if (filter === 'all') return true;
    const text = scheduleSearchText(schedule);
    if (filter === 'jobs') return !!schedule.jobApplicationId || schedule.isBmSchedule === true;
    if (filter === 'interview') return /interview/.test(text);
    if (filter === 'new-joiner') return /new join|new staff|onboard/.test(text);
    if (filter === 'transport') return /transport|driver|vehicle|trip/.test(text);
    if (filter === 'compliance') return /compliance|permit|licen[cs]e|renewal/.test(text);
    if (filter === 'notice') return /notice|announcement/.test(text);
    if (filter === 'company-event') return /company event|event|meeting|training/.test(text);
    if (filter === 'holiday') return isHolidayTitle(schedule.title);
    return isLeaveOnlyRow(schedule) || /leave|\bmc\b|medical/.test(text);
  };

  const scheduleMatchesJobType = (schedule: ScheduleItem, filter: ScheduleJobTypeFilter): boolean => {
    if (filter === 'all') return true;
    const text = scheduleSearchText(schedule);
    if (filter === 'delivery') return schedule.scheduleRecordType === 'delivery';
    if (filter === 'site-visit') return isSiteVisitSchedule(schedule);
    if (filter === 'touch-up') return /touch[\s-]?up/.test(text);
    return schedule.isBmSchedule === true && schedule.scheduleRecordType !== 'delivery' && !isSiteVisitSchedule(schedule);
  };

  const scheduleMatchesDateFilter = (schedule: ScheduleItem, filters: ScheduleListFilters): boolean => {
    if (filters.date === 'all') return true;
    const rowDate = parseApiDateOnly(schedule.scheduleDate || schedule.date);
    if (!rowDate) return false;
    const rowIso = formatDateForApi(rowDate);
    const today = new Date();
    const todayKey = formatDateForApi(today);
    if (filters.date === 'today') return rowIso === todayKey;
    if (filters.date === 'next-7') {
      const lastDay = new Date(today);
      lastDay.setDate(lastDay.getDate() + 6);
      return rowIso >= todayKey && rowIso <= formatDateForApi(lastDay);
    }
    if (filters.date === 'upcoming') return rowIso >= todayKey;
    return (!filters.fromDate || rowIso >= filters.fromDate) &&
      (!filters.toDate || rowIso <= filters.toDate);
  };

  const scheduleIsPending = (schedule: ScheduleItem): boolean =>
    !schedule.isDeliveryCompleted &&
    (
      schedule.workFlowStatusId === 0 ||
      schedule.canJobCheckIn === true ||
      schedule.canTripCheckIn === true ||
      (!schedule.clockIn && !schedule.clockOut)
    );

  const scheduleIsScheduled = (schedule: ScheduleItem): boolean =>
    !schedule.isDeliveryCompleted && !schedule.clockOut && !scheduleIsPending(schedule);

  const listWorkerOptions = Array.from(new Set(
    allJobCards
      .map((schedule) => schedule.workerDisplayName?.trim())
      .filter((worker): worker is string => !!worker),
  )).sort((a, b) => a.localeCompare(b));

  const searchedJobCards = allJobCards.filter((schedule) => {
    const query = listSearch.trim().toLowerCase();
    return !query || scheduleSearchText(schedule).includes(query);
  });

  const quickFilterCounts: Record<ScheduleQuickFilter, number> = {
    all: searchedJobCards.length,
    today: searchedJobCards.filter((schedule) => {
      const rowDate = parseApiDateOnly(schedule.scheduleDate || schedule.date);
      return !!rowDate && formatDateForApi(rowDate) === todayIso;
    }).length,
    scheduled: searchedJobCards.filter(scheduleIsScheduled).length,
    pending: searchedJobCards.filter(scheduleIsPending).length,
  };

  const jobCards = searchedJobCards.filter((schedule) => {
    if (viewMode !== 'list') return true;
    const rowDate = parseApiDateOnly(schedule.scheduleDate || schedule.date);
    if (listQuickFilter === 'today' && (!rowDate || formatDateForApi(rowDate) !== todayIso)) return false;
    if (listQuickFilter === 'scheduled' && !scheduleIsScheduled(schedule)) return false;
    if (listQuickFilter === 'pending' && !scheduleIsPending(schedule)) return false;
    if (!scheduleMatchesShowFilter(schedule, listFilters.show)) return false;
    if (!scheduleMatchesDateFilter(schedule, listFilters)) return false;
    if (!scheduleMatchesJobType(schedule, listFilters.jobType)) return false;
    if (listFilters.worker && schedule.workerDisplayName !== listFilters.worker) return false;
    return true;
  });

  const activeListFilterCount = [
    listFilters.show !== 'all',
    listFilters.date !== 'all',
    listFilters.jobType !== 'all',
    !!listFilters.worker,
  ].filter(Boolean).length;

  const openListFilters = () => {
    setDraftListFilters({ ...listFilters });
    setShowListFilter(true);
  };

  const clearListFilters = () => {
    setDraftListFilters({ ...EMPTY_LIST_FILTERS });
  };

  const applyListFilters = () => {
    if (
      draftListFilters.date === 'custom' &&
      draftListFilters.fromDate &&
      draftListFilters.toDate &&
      draftListFilters.fromDate > draftListFilters.toDate
    ) {
      toast.error('The From date must be before the To date');
      return;
    }
    if (
      draftListFilters.date === 'custom' &&
      draftListFilters.fromDate &&
      draftListFilters.toDate
    ) {
      const fromDate = parseApiDateOnly(draftListFilters.fromDate);
      const toDate = parseApiDateOnly(draftListFilters.toDate);
      if (fromDate && toDate && (toDate.getTime() - fromDate.getTime()) / 86400000 > 62) {
        toast.error('Choose a date range of 63 days or less');
        return;
      }
    }
    setListFilters({ ...draftListFilters });
    setShowListFilter(false);
  };

  const getDayCellClasses = (iso: string, isToday: boolean, isSelected: boolean): string => {
    const info = leaveDayMap[iso];
    const classes = ['schedule-cal-day'];
    if (isToday) {
      classes.push('today');
    } else if (info?.kind === 'mc') {
      classes.push('leave-mc');
    } else if (info?.kind === 'leave') {
      classes.push('leave-annual');
    } else if (holidayDays.has(iso)) {
      classes.push('holiday');
    } else if (isSelected) {
      classes.push('selected');
    }
    if (isSelected && !isToday) classes.push('selected-border');
    return classes.join(' ');
  };

  const getDayStateTag = (iso: string, isToday: boolean): string => {
    if (isToday) return '';
    const info = leaveDayMap[iso];
    if (info?.kind === 'mc') return 'MC';
    if (info?.kind === 'leave') return 'Leave';
    if (holidayDays.has(iso)) return 'PH';
    return '';
  };

  const openSiteVisitReport = (schedule: ScheduleItem) => {
    const existing = siteVisitReports[schedule.id];
    setSiteVisitReportSchedule(schedule);
    setSiteVisitDraft(existing || {
      scheduleId: schedule.id,
      findings: '',
      equipment: '',
      recommendation: '',
      requirements: [''],
      photos: [],
    });
    setShowSiteVisitReport(true);
  };

  const handleSiteVisitPhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const src = String(reader.result || '');
        if (!src) return;
        setSiteVisitDraft((prev) => ({ ...prev, photos: [...prev.photos, src] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleSubmitSiteVisitReport = () => {
    if (!siteVisitReportSchedule) return;
    if (!siteVisitDraft.findings.trim()) {
      toast.error('Please record your findings');
      return;
    }
    const reqs = siteVisitDraft.requirements.map((x) => x.trim()).filter(Boolean);
    const draft: SiteVisitReportDraft = {
      ...siteVisitDraft,
      scheduleId: siteVisitReportSchedule.id,
      requirements: reqs,
      submittedAt: new Date().toISOString(),
    };
    writeSiteVisitReport(draft);
    setSiteVisitReports((prev) => ({ ...prev, [draft.scheduleId]: draft }));
    setShowSiteVisitReport(false);
    setSiteVisitReportSchedule(null);
    toast.success('Site visit report saved');
  };

  const previewServiceReport = async (scheduleId: string) => {
    if (printingReportId) return;

    const previewWindow = window.open('', '_blank');
    try {
      setPrintingReportId(scheduleId);
      const response = await scheduleAPI.printServiceReport(scheduleId);
      const pdf = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(pdf);

      if (previewWindow) {
        previewWindow.location.href = blobUrl;
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      }

      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error: any) {
      previewWindow?.close();
      console.error('Error printing service report:', error);
      toast.error(
        error?.response?.data?.message ||
        'Failed to open the service report. Please try again.'
      );
    } finally {
      setPrintingReportId(null);
    }
  };

  const openCompanyPath = async (pathAndQuery: string, hash = '') => {
    const targetWindow = window.open('about:blank', '_blank');
    if (targetWindow) targetWindow.opener = null;
    try {
      const baseUrl = await scheduleAPI.getCompanyApplicationUrl();
      if (!baseUrl) throw new Error('Company.Application URL is not configured');
      const url = `${baseUrl}${pathAndQuery}${hash}`;
      if (targetWindow) {
        targetWindow.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      targetWindow?.close();
      toast.error(error?.response?.data?.message || error?.message || 'Unable to open Company.Application');
    }
  };

  const openCompanyServiceReport = async (schedule: ScheduleItem) => {
    if (!schedule.businessScheduleId || (!schedule.assignmentId && !schedule.sitePicId)) {
      toast.error('This service job is not linked to your worker or Site PIC assignment');
      return;
    }

    setJobActionSchedule(null);
    setServiceCompletionLoading(true);
    try {
      const detail = await scheduleAPI.getServiceCompletion(
        schedule.businessScheduleId,
        schedule.assignmentId,
        schedule.sitePicId,
      );
      setServiceCompletionDetail(detail);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to load the catalog service report');
    } finally {
      setServiceCompletionLoading(false);
    }
  };

  const printBusinessServiceReport = async (detail: ServiceCompletionDetail) => {
    if (printingReportId) return;
    const previewWindow = window.open('', '_blank');
    try {
      setPrintingReportId(String(detail.id));
      const response = await scheduleAPI.printBusinessServiceReport(
        detail.id,
        detail.assignmentId,
        detail.sitePicId,
      );
      const reportUrl = String(response.data?.url || '');
      if (!reportUrl) throw new Error('Ultra POS report link was not returned');
      if (previewWindow) previewWindow.location.href = reportUrl;
      else window.location.href = reportUrl;
    } catch (error: any) {
      previewWindow?.close();
      toast.error(error?.response?.data?.message || 'Unable to print the service report');
    } finally {
      setPrintingReportId(null);
    }
  };

  const openScheduleActivityLog = async (schedule: ScheduleItem) => {
    if (!schedule.isBmSchedule || !schedule.businessScheduleId) return;

    setScheduleActivityLoading(true);
    try {
      const result = await scheduleAPI.getScheduleActivityLog(
        schedule.businessScheduleId,
        schedule.assignmentId,
        schedule.sitePicId,
      );
      setScheduleActivity({
        title: result?.title || schedule.documentNo || 'Schedule activity',
        items: Array.isArray(result?.items) ? result.items : [],
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to load schedule activity');
    } finally {
      setScheduleActivityLoading(false);
    }
  };

  const openJobPhotoSheet = async (schedule: ScheduleItem) => {
    if (!schedule.assignmentId) {
      toast.error('This job does not have a worker assignment for photo upload');
      return;
    }
    setJobActionSchedule(null);
    setJobPhotoSchedule(schedule);
    setServiceJobPhotos([]);
    setServicePhotoLoading(true);
    try {
      const photos = await scheduleAPI.getServicePhotos(schedule.assignmentId, schedule.sitePicId);
      setServiceJobPhotos(photos);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to load job photos');
    } finally {
      setServicePhotoLoading(false);
    }
  };

  const handleServicePhotoAdd = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'before' | 'after',
  ) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!jobPhotoSchedule?.assignmentId || !files.length || servicePhotoUploading) return;

    const oversized = files.find((file) => file.size > 10 * 1024 * 1024);
    if (oversized) {
      toast.error('Each photo must be 10 MB or smaller');
      return;
    }

    setServicePhotoUploading(true);
    try {
      for (const file of files) {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const saved = await scheduleAPI.uploadServicePhoto(
          jobPhotoSchedule.assignmentId,
          { type, dataUrl },
          jobPhotoSchedule.sitePicId,
        );
        setServiceJobPhotos((current) => [...current, saved]);
      }
      toast.success(`${type === 'before' ? 'Before' : 'After'} photo uploaded`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to upload job photo');
    } finally {
      setServicePhotoUploading(false);
    }
  };

  const openOtRequest = (schedule: ScheduleItem) => {
    if (schedule.isServiceCompleted) {
      toast.info('This completed job is locked. Only the service report can be printed.');
      return;
    }
    if (!schedule.assignmentId) {
      toast.error('This job does not have a worker assignment for an OT request');
      return;
    }
    setJobActionSchedule(null);
    setOtSchedule(schedule);
    setOtHours('1');
    setOtReason('');
  };

  const submitOtRequest = async () => {
    if (!otSchedule?.assignmentId || otSubmitting) return;
    const hours = Number(otHours);
    if (!Number.isInteger(hours) || hours < 1 || hours > 24) {
      toast.error('Enter OT hours between 1 and 24');
      return;
    }
    if (!otReason.trim()) {
      toast.error('Enter the reason for OT');
      return;
    }

    setOtSubmitting(true);
    try {
      await scheduleAPI.requestOvertime(
        otSchedule.assignmentId,
        { hours, reason: otReason.trim() },
        otSchedule.sitePicId,
      );
      toast.success('OT request sent to your manager');
      setOtSchedule(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to submit OT request');
    } finally {
      setOtSubmitting(false);
    }
  };

  const openExtraService = async (schedule: ScheduleItem) => {
    setJobActionSchedule(null);
    if (schedule.isServiceCompleted) {
      toast.info('This completed job is locked. Extra services can no longer be added.');
      return;
    }
    if (!schedule.businessScheduleId || (!schedule.assignmentId && !schedule.sitePicId)) {
      toast.error('This job is not linked to your schedule assignment');
      return;
    }
    setExtraServiceSchedule(schedule);
    setExtraServiceDetail(null);
    setExtraServiceSearch('');
    setExtraServiceSelections({});
    setExtraServiceLoading(true);
    try {
      const detail = await scheduleAPI.getExtraService(
        schedule.businessScheduleId,
        schedule.assignmentId,
        schedule.sitePicId,
      );
      setExtraServiceDetail(detail);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to load service catalog');
      setExtraServiceSchedule(null);
    } finally {
      setExtraServiceLoading(false);
    }
  };

  const submitExtraService = async () => {
    if (!extraServiceSchedule?.businessScheduleId || !extraServiceDetail || extraServiceSubmitting) return;
    const selectedLines = (extraServiceDetail.items || [])
      .filter((item) => Object.prototype.hasOwnProperty.call(extraServiceSelections, item.id))
      .map((item) => ({ businessItemId: item.id, quantity: Number(extraServiceSelections[item.id]) }));
    if (!selectedLines.length) {
      toast.error('Select at least one service item');
      return;
    }
    if (selectedLines.some((line) => !Number.isFinite(line.quantity) || line.quantity <= 0 || line.quantity > 9999)) {
      toast.error('Each selected service needs a quantity between 0.01 and 9,999');
      return;
    }

    setExtraServiceSubmitting(true);
    try {
      const saved = await scheduleAPI.addExtraService(
        extraServiceSchedule.businessScheduleId,
        extraServiceSchedule.assignmentId,
        extraServiceSchedule.sitePicId,
        { items: selectedLines },
      );
      setExtraServiceDetail((current) => current ? {
        ...current,
        currentSubtotal: saved.subtotal,
        currentTax: saved.tax,
        currentTotal: saved.total,
        balanceAmount: saved.balance,
        activeLineCount: saved.activeLineCount,
      } : current);
      setExtraServiceSearch('');
      setExtraServiceSelections({});
      toast.success(`${saved.addedCount} service${saved.addedCount === 1 ? '' : 's'} added to ${extraServiceDetail.documentNo}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to add extra service');
    } finally {
      setExtraServiceSubmitting(false);
    }
  };

  const openDeliveryCompletion = async (schedule: ScheduleItem) => {
    if (!schedule.deliveryOrderId) {
      toast.error('This delivery order is not linked');
      return;
    }

    setShowDeliveryCompletion(true);
    setDeliveryCompletionStage('edit');
    setDeliveryCompletionLoading(true);
    setDeliveryCompletionDraft(null);
    try {
      const detail = await scheduleAPI.getDeliveryCompletion(schedule.deliveryOrderId);
      if (detail.isCompleted) {
        toast.info('This delivery has already been completed');
        setShowDeliveryCompletion(false);
        return;
      }
      setDeliveryCompletionDraft({
        deliveryOrderId: detail.id,
        documentNo: detail.documentNo || schedule.documentNo || `DO-${detail.id}`,
        customerName: detail.customerName || schedule.siteName || 'Recipient',
        deliveryDate: detail.deliveryDate,
        checkedInDateTime: detail.checkedInDateTime,
        receivedBy: detail.receivedBy || detail.customerName || '',
        notes: detail.notes || '',
        lines: (detail.lines || []).map((line: any) => ({
          id: Number(line.id),
          name: line.name || line.description || 'Delivery item',
          description: line.description || '',
          orderedQuantity: Number(line.orderedQuantity || 0),
          receivedQuantity: Number(line.orderedQuantity || 0),
          confirmed: true,
        })),
        photos: [],
        latitude: null,
        longitude: null,
      });
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => setDeliveryCompletionDraft((prev) => prev ? ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }) : prev),
          () => undefined,
          { enableHighAccuracy: true, timeout: 10000 },
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to load the delivery order');
      setShowDeliveryCompletion(false);
    } finally {
      setDeliveryCompletionLoading(false);
    }
  };

  const handleDeliveryPhotoAdd = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const photos = await Promise.all(files.map((file) => new Promise<DeliveryCompletionPhoto>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ dataUrl: String(reader.result || ''), caption: '' });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    })));
    setDeliveryCompletionDraft((prev) => prev ? ({ ...prev, photos: [...prev.photos, ...photos] }) : prev);
  };

  const reviewDeliveryCompletion = () => {
    if (!deliveryCompletionDraft) return;
    if (!deliveryCompletionDraft.receivedBy.trim()) {
      toast.error('Received by name is required');
      return;
    }
    if (!deliveryCompletionDraft.lines.length) {
      toast.error('This delivery order has no items');
      return;
    }
    const invalidLine = deliveryCompletionDraft.lines.find((line) =>
      line.receivedQuantity < 0 || line.receivedQuantity > line.orderedQuantity
    );
    if (invalidLine) {
      toast.error(`Received quantity for ${invalidLine.name} must be between 0 and ${invalidLine.orderedQuantity}`);
      return;
    }
    if (!deliveryCompletionDraft.photos.length) {
      toast.error('Add at least one proof-of-delivery photo');
      return;
    }
    setDeliveryCompletionStage('confirm');
  };

  const submitDeliveryCompletion = async (skipSignature = false) => {
    if (!deliveryCompletionDraft || deliveryCompletionSubmitting) return;
    let signatureDataUrl = '';
    if (!skipSignature) {
      const pad = deliverySignatureRef.current;
      if (!pad || pad.isEmpty()) {
        toast.error('Ask the recipient to sign, or use the POD photo option');
        return;
      }
      // react-signature-canvas@1.1's getTrimmedCanvas() currently fails in
      // production builds because its trim-canvas dependency is not callable.
      // Exporting the underlying canvas preserves the signature and avoids
      // blocking delivery completion.
      try {
        signatureDataUrl = pad.getCanvas().toDataURL('image/png');
      } catch (error) {
        console.error('Unable to export recipient signature', error);
        toast.error('Unable to process the signature. Please clear it and sign again.');
        return;
      }
    }

    setDeliveryCompletionSubmitting(true);
    try {
      await scheduleAPI.completeDelivery(deliveryCompletionDraft.deliveryOrderId, {
        receivedBy: deliveryCompletionDraft.receivedBy.trim(),
        notes: deliveryCompletionDraft.notes.trim(),
        latitude: deliveryCompletionDraft.latitude,
        longitude: deliveryCompletionDraft.longitude,
        signatureDataUrl,
        lines: deliveryCompletionDraft.lines.map((line) => ({
          lineId: line.id,
          receivedQuantity: line.confirmed ? line.receivedQuantity : 0,
        })),
        photos: deliveryCompletionDraft.photos,
      });
      toast.success('Delivery completed');
      setShowDeliveryCompletion(false);
      setDeliveryCompletionDraft(null);
      setPageNumber(1);
      setSchedules([]);
      await fetchSchedules(false, 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to complete the delivery');
    } finally {
      setDeliveryCompletionSubmitting(false);
    }
  };

  const getScheduleStatus = (schedule: ScheduleItem): { label: string; tone: 'pending' | 'done' | 'progress' } => {
    const reportDone = !!siteVisitReports[schedule.id]?.submittedAt;
    if (schedule.scheduleType === 'attendance') {
      return schedule.clockOut
        ? { label: 'Clocked Out', tone: 'done' }
        : { label: 'Clocked In', tone: 'progress' };
    }
    if (schedule.isBmSchedule) {
      if (schedule.scheduleRecordType === 'delivery') {
        return schedule.isDeliveryCompleted
          ? { label: 'Delivered', tone: 'done' }
          : { label: 'Delivery', tone: 'progress' };
      }
      if (isBmSchedulePending(schedule)) return { label: 'Pending', tone: 'pending' };
      if (schedule.isServiceCompleted) return { label: 'Completed', tone: 'done' };
      if (reportDone) return { label: 'Completed', tone: 'done' };
      if (schedule.isJobCheckedIn || schedule.isTripCheckedIn) return { label: 'Checked In', tone: 'progress' };
      return { label: 'Scheduled', tone: 'progress' };
    }
    if (isScheduleCompleted(schedule) || reportDone) return { label: 'Completed', tone: 'done' };
    if (isScheduleInProgress(schedule)) return { label: 'In Progress', tone: 'progress' };
    if (canCheckIn(schedule)) return { label: 'Pending', tone: 'pending' };
    return { label: 'Scheduled', tone: 'progress' };
  };

  const renderScheduleCard = (schedule: ScheduleItem, index: number) => {
    const status = getScheduleStatus(schedule);
    const reportDone = !!siteVisitReports[schedule.id]?.submittedAt;
    const siteVisit = isSiteVisitSchedule(schedule);
    const isDelivery = schedule.scheduleRecordType === 'delivery' && !!schedule.deliveryOrderId;
    const isAttendance = schedule.scheduleType === 'attendance';
    const isCompletedBmService = schedule.isBmSchedule
      && !isDelivery
      && !!schedule.businessScheduleId
      && !!schedule.isServiceCompleted;
    const isActiveBmService = schedule.isBmSchedule
      && !isDelivery
      && !schedule.canJobCheckIn
      && !schedule.canTripCheckIn
      && !!schedule.businessScheduleId
      && !schedule.isServiceCompleted;
    const displayName = schedule.siteName || schedule.title || schedule.projectName || schedule.customerName || 'Schedule item';
    const contactLine = schedule.contactPerson
      ? `${displayName} · ${schedule.contactPerson}`
      : displayName;
    const cardDateTime = schedule.isBmSchedule
      ? formatBmScheduleDateTime(schedule.scheduleDate || schedule.date, schedule.scheduledTime || schedule.time)
      : `${schedule.time || 'No time'}${schedule.date ? ` · ${schedule.date}` : ''}`;

    return (
      <div key={`${schedule.id}-${index}`} className="schedule-job-card-group">
      <div className="schedule-job-card">
        {schedule.isBmSchedule && schedule.businessScheduleId ? (
          <button
            type="button"
            className="schedule-job-card-head schedule-card-activity-row"
            aria-label={`View activity log for ${cardDateTime}, ${status.label}`}
            disabled={scheduleActivityLoading}
            onClick={() => void openScheduleActivityLog(schedule)}
          >
            <span className="schedule-card-static-icon" aria-hidden="true">{getScheduleCardIcon(schedule)}</span>
            <span className="schedule-job-card-time">{cardDateTime}</span>
            <span className={`schedule-status-pill ${status.tone}`}>{status.label}</span>
          </button>
        ) : (
          <div className="schedule-job-card-head">
            <span className="schedule-card-static-icon" aria-hidden="true">{getScheduleCardIcon(schedule)}</span>
            <span className="schedule-job-card-time">{cardDateTime}</span>
            <span className={`schedule-status-pill ${status.tone}`}>{status.label}</span>
          </div>
        )}

        <div className="schedule-job-client">{contactLine}</div>

        {schedule.contactNo ? (
          <a
            href={`https://wa.me/${formatWhatsAppNumber(schedule.contactNo)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="schedule-job-phone"
            onClick={(e) => e.stopPropagation()}
          >
            <span aria-hidden="true">📞</span>
            {schedule.contactNo}
          </a>
        ) : null}

        {schedule.address ? (
          <div className="schedule-job-address" onClick={() => handleAddressClick(schedule.address)}>
            <span className="schedule-job-address-pin" aria-hidden="true">📍</span>
            <span>{schedule.address}</span>
          </div>
        ) : schedule.remarks ? (
          <div className="schedule-remarks text-xs text-gray-700 mt-2" dangerouslySetInnerHTML={{ __html: schedule.remarks }} />
        ) : null}

        {(reportDone || status.label === 'Delivered' || (status.label === 'Completed' && !schedule.isBmSchedule)) && (
          <div className={`schedule-job-progress ${reportDone ? 'done' : 'pending'}`}>
            {reportDone
              ? '✓ Completed · report submitted'
              : status.label === 'Delivered'
                ? '✓ Delivered · recipient confirmed'
              : status.label === 'Completed'
                ? '✓ Completed · report pending'
                : ''}
          </div>
        )}

        {schedule.jobApplicationId != null && schedule.jobApplicationId > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => void openCompanyPath(
                `/JobApplication/Index?openView=${encodeURIComponent(String(schedule.jobApplicationId))}`,
                '#managementTab',
              )}
              className="text-xs text-primary font-semibold underline-offset-2 hover:underline"
            >
              Open in Ultra POS (View application)
            </button>
          </div>
        )}
      </div>

        <div className={`schedule-job-actions schedule-job-actions-outside ${isActiveBmService ? 'direct-service-actions' : ''}`}>
          {isAttendance ? (
            !schedule.clockOut ? (
              <button
                type="button"
                className="schedule-checkin-btn"
                disabled={isSubmitting}
                onClick={() => void handleAttendanceClockOut(schedule)}
              >
                Clock Out
              </button>
            ) : null
          ) : isCompletedBmService ? (
            <button
              type="button"
              className="schedule-checkin-btn schedule-print-report-btn"
              disabled={printingReportId === schedule.id}
              onClick={() => void openCompanyServiceReport(schedule)}
            >
              <span aria-hidden="true">📄</span>
              <span>{printingReportId === schedule.id ? 'Opening report…' : 'Print service report'}</span>
            </button>
          ) : isActiveBmService ? (
            <>
              <button type="button" className="schedule-direct-action" onClick={() => void openJobPhotoSheet(schedule)}>
                <span aria-hidden="true">📷</span>
                <span className="schedule-direct-action-label">Photos</span>
              </button>
              <button type="button" className="schedule-direct-action" onClick={() => openOtRequest(schedule)}>
                <span aria-hidden="true">⏱</span>
                <span className="schedule-direct-action-label">Request OT</span>
              </button>
              <button type="button" className="schedule-direct-action" onClick={() => openExtraService(schedule)}>
                <span aria-hidden="true">📜</span>
                <span className="schedule-direct-action-label">Extra service</span>
              </button>
              <button type="button" className="schedule-checkin-btn schedule-complete-job-btn" onClick={() => void openCompanyServiceReport(schedule)}>
                <span aria-hidden="true">✓</span>
                <span className="schedule-direct-action-label">Complete job</span>
              </button>
            </>
          ) : (
            <>
              {!isDelivery ? (
                <button
                  type="button"
                  className={`schedule-icon-btn ${schedule.isBmSchedule || siteVisit || reportDone || status.label === 'Completed' ? 'active' : ''}`}
                  aria-label={schedule.isBmSchedule ? 'View service report' : (siteVisit ? 'Site visit report' : 'Preview service report')}
                  disabled={printingReportId === schedule.id || (schedule.isBmSchedule && !schedule.businessScheduleId) || (!siteVisit && !schedule.isBmSchedule && status.label !== 'Completed')}
                  onClick={() => {
                    if (schedule.isBmSchedule) void openCompanyServiceReport(schedule);
                    else if (siteVisit) openSiteVisitReport(schedule);
                    else void previewServiceReport(schedule.id);
                  }}
                >📄</button>
              ) : null}
              {schedule.isBmSchedule && !isDelivery && schedule.assignmentId ? (
                <button type="button" className="schedule-icon-btn" aria-label="Before and after photos" onClick={() => void openJobPhotoSheet(schedule)}>📷</button>
              ) : null}
              {schedule.isBmSchedule ? (
                <>
                  {isDelivery && !schedule.isDeliveryCompleted ? <button type="button" className="schedule-checkin-btn" onClick={() => void openDeliveryCompletion(schedule)}>Complete delivery</button> : null}
                  {schedule.canJobCheckIn && schedule.assignmentId ? <button type="button" className="schedule-checkin-btn outline" onClick={() => openBmCheckInModal(schedule, 'job')} disabled={isSubmitting}>Job check in</button> : null}
                  {schedule.canTripCheckIn && schedule.sitePicId ? <button type="button" className="schedule-checkin-btn" onClick={() => openBmCheckInModal(schedule, 'trip')} disabled={isSubmitting}>Trip check in</button> : null}
                </>
              ) : canCheckIn(schedule) ? (
                <button type="button" className="schedule-checkin-btn" onClick={(e) => handleCheckIn(e, schedule.id)} disabled={isSubmitting}>Check in</button>
              ) : schedule.id && /^\d+$/.test(String(schedule.id)) ? (
                <button type="button" className="schedule-checkin-btn outline" onClick={() => navigate(`/schedule/${schedule.id}`)}>View details</button>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  const calendarCells = (() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startDow = first.getDay();
    const cells: Array<{ key: string; day?: number; iso?: string; muted?: boolean }> = [];

    for (let i = 0; i < startDow; i++) {
      const pd = new Date(y, m, -(startDow - i - 1));
      cells.push({ key: `p-${i}`, day: pd.getDate(), muted: true });
    }
    for (let d = 1; d <= last.getDate(); d++) {
      const iso = formatDateForApi(new Date(y, m, d));
      cells.push({ key: iso, day: d, iso });
    }
    const rem = (7 - ((startDow + last.getDate()) % 7)) % 7;
    for (let nd = 1; nd <= rem; nd++) {
      cells.push({ key: `n-${nd}`, day: nd, muted: true });
    }
    return cells;
  })();

  const monthEventTotal = Object.values(eventDayCounts).reduce((total, count) => total + count, 0);
  const headerJobLabel = `${monthEventTotal} job${monthEventTotal === 1 ? '' : 's'}`;
  const attendanceClockIn = todayAttendance?.clockIn ? new Date(todayAttendance.clockIn) : null;
  const attendanceClockOut = todayAttendance?.clockOut ? new Date(todayAttendance.clockOut) : null;
  const attendanceTitle = attendanceClockIn
    ? (attendanceClockOut ? 'Clocked out' : 'Clocked in')
    : 'Not clocked in';
  const attendanceSub = attendanceClockIn
    ? `${attendanceClockOut ? 'Finished' : 'Started'} ${format(attendanceClockOut || attendanceClockIn, 'HH:mm')}`
    : `Now ${format(scheduleNow, 'HH:mm')}`;
  const extraServiceTerm = extraServiceSearch.trim().toLowerCase();
  const filteredExtraServiceItems = (extraServiceDetail?.items || []).filter((item) =>
    !extraServiceTerm ||
    item.name.toLowerCase().includes(extraServiceTerm) ||
    String(item.code || '').toLowerCase().includes(extraServiceTerm) ||
    String(item.description || '').toLowerCase().includes(extraServiceTerm));
  const selectedExtraServiceItems = (extraServiceDetail?.items || [])
    .filter((item) => Object.prototype.hasOwnProperty.call(extraServiceSelections, item.id));
  const extraServiceSelectionTotal = selectedExtraServiceItems.reduce((total, item) => {
    const quantity = Math.max(0, Number(extraServiceSelections[item.id]) || 0);
    const taxRate = item.taxRate > 0 && item.taxRate <= 1 ? item.taxRate * 100 : Math.max(0, item.taxRate);
    const subtotal = item.price * quantity;
    return total + subtotal + (subtotal * taxRate / 100);
  }, 0);
  const money = (value: number) => `RM ${Number(value || 0).toFixed(2)}`;

  return (
    <div className="schedule-page container-app py-2 pb-24 md:pb-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="schedule-page-header">
        <div>
          <div className="schedule-page-title">Schedule</div>
          <div className="schedule-page-sub">{headerJobLabel} · from Operation Mgmt</div>
        </div>
        <div className="schedule-view-toggle">
          <button
            type="button"
            className={`schedule-view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewMode('calendar')}
            title="Calendar view"
            aria-label="Calendar view"
          >
            <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          </button>
          <button
            type="button"
            className={`schedule-view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
            aria-label="List view"
          >
            <svg viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
          </button>
        </div>
      </div>

      <div className="schedule-attendance-card">
        <div>
          <div className="schedule-attendance-title">{attendanceTitle}</div>
          <div className="schedule-attendance-sub">{attendanceSub}</div>
        </div>
        <button type="button" className="schedule-attendance-btn" onClick={() => navigate('/checkin')}>
          {attendanceClockIn && !attendanceClockOut ? 'Clock Out' : 'Clock In'}
        </button>
      </div>

      {viewMode === 'list' && (
        <div className="schedule-list-controls">
          <div className="schedule-list-search-row">
            <label className="schedule-list-search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="20" y1="20" x2="16.65" y2="16.65" />
              </svg>
              <span className="sr-only">Search schedules</span>
              <input
                type="search"
                value={listSearch}
                onChange={(event) => setListSearch(event.target.value)}
                placeholder="Search name, #, address, service"
                autoComplete="off"
              />
            </label>
            <button type="button" className="schedule-list-filter-btn" onClick={openListFilters}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filter</span>
              {activeListFilterCount > 0 ? <i>{activeListFilterCount}</i> : null}
            </button>
          </div>
          <div className="schedule-list-quick-tabs" role="group" aria-label="Schedule status filters">
            {([
              ['all', 'All'],
              ['today', 'Today'],
              ['scheduled', 'Scheduled'],
              ['pending', 'Pending'],
            ] as Array<[ScheduleQuickFilter, string]>).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={listQuickFilter === value ? 'active' : ''}
                onClick={() => setListQuickFilter(value)}
                aria-pressed={listQuickFilter === value}
              >
                {label} ({quickFilterCounts[value]})
              </button>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'calendar' && (
        <>
          <div className="schedule-cal-nav">
            <button type="button" className="schedule-cal-nav-btn" onClick={prevMonth} aria-label="Previous month">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="schedule-cal-month">{format(currentMonth, 'MMMM yyyy')}</div>
            <button type="button" className="schedule-cal-nav-btn" onClick={nextMonth} aria-label="Next month">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          <div className="schedule-cal-grid-wrap">
            <div className="schedule-cal-dow">
              {WEEKDAY_SHORT.map((d) => <span key={d}>{d}</span>)}
            </div>
            <div className="schedule-cal-grid">
              {calendarCells.map((cell) => {
                if (!cell.iso) {
                  return (
                    <div key={cell.key} className="schedule-cal-day muted">
                      {cell.day}
                    </div>
                  );
                }
                const iso = cell.iso;
                const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), cell.day || 1);
                const isToday = iso === todayIso;
                const isSelected = iso === selectedIso;
                const stateTag = getDayStateTag(iso, isToday);
                const eventCount = eventDayCounts[iso] || 0;

                return (
                  <button
                    key={cell.key}
                    type="button"
                    className={getDayCellClasses(iso, isToday, isSelected)}
                    onClick={() => handleDateSelect(dayDate)}
                  >
                    {cell.day}
                    {stateTag ? <div className="schedule-cal-day-tag">{stateTag}</div> : null}
                    {eventCount > 0 ? <div className="schedule-cal-day-badge">{eventCount}</div> : null}
                  </button>
                );
              })}
            </div>
            <div className="schedule-cal-legend" aria-label="Calendar legend">
              <span><i className="holiday" />Public holiday</span>
              <span><i className="medical" />Medical leave</span>
              <span><i className="leave" />Leave</span>
              <span><i className="today" />Today</span>
            </div>
          </div>
        </>
      )}

      <div id="schedule-day-events" className={`schedule-day-heading ${viewMode === 'list' ? 'list-mode' : ''}`}>
        <div>
          <span className="schedule-day-heading-title">
            {viewMode === 'list'
              ? 'Schedule list'
              : formatNiceDayLabel(activeSelectedDate, selectedIso === todayIso)}
          </span>
          <span className="schedule-day-heading-count">
            {jobCards.length} item{jobCards.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {selectedLeaveInfo ? (
        <div className={`schedule-day-banner ${selectedLeaveInfo.kind === 'mc' ? 'mc' : 'leave'}`}>
          {selectedLeaveInfo.kind === 'mc' ? '🏥 On medical leave (MC)' : '🌿 On approved leave'}
        </div>
      ) : null}

      {selectedHolidayBanner ? (
        <div className="schedule-day-banner holiday">🎉 Public holiday · {selectedHolidayBanner}</div>
      ) : null}

      {loading && pageNumber === 1 ? (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          <p className="mt-2 text-gray-600">Loading schedules...</p>
        </div>
      ) : jobCards.length > 0 ? (
        <>
          {jobCards.map((schedule, index) => renderScheduleCard(schedule, index))}

          {isLoadingMore && (
            <div className="p-4 text-center">
              <div className="inline-block animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
              <p className="mt-2 text-sm text-gray-600">Loading more...</p>
            </div>
          )}

          {!hasMore && !isLoadingMore && jobCards.length > 0 && (
            <div className="p-4 text-center">
              <p className="text-sm text-gray-600">No more schedules to load</p>
            </div>
          )}
        </>
      ) : (
        <div className="p-8 text-center bg-white rounded-xl border border-gray-200 mx-4">
          <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-4 text-gray-600">Nothing on this day</p>
        </div>
      )}

      {/* Service job actions, matching the mobile merchant workflow reference. */}
      {jobActionSchedule && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setJobActionSchedule(null)} />
          <section
            className="schedule-sheet schedule-job-action-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-job-action-title"
          >
            <div className="schedule-sheet-handle" />
            <header className="schedule-sheet-header">
              <div className="schedule-sheet-title" id="schedule-job-action-title">
                {jobActionSchedule.siteName || jobActionSchedule.customerName || 'Complete job'}
                {jobActionSchedule.documentNo ? ` · ${jobActionSchedule.documentNo}` : ''}
              </div>
              <button
                type="button"
                className="schedule-sheet-close"
                onClick={() => setJobActionSchedule(null)}
                aria-label="Close job actions"
              >
                ×
              </button>
            </header>

            <div className="schedule-sheet-body schedule-job-action-body">
              <button type="button" className="schedule-job-action-row" onClick={() => void openJobPhotoSheet(jobActionSchedule)}>
                <span className="schedule-job-action-icon" aria-hidden="true">📷</span>
                <span className="schedule-job-action-copy">
                  <strong>Before / after photos</strong>
                  <small>Add photo evidence for this job</small>
                </span>
                <span className="schedule-job-action-chevron" aria-hidden="true">›</span>
              </button>
              <button type="button" className="schedule-job-action-row" onClick={() => openOtRequest(jobActionSchedule)}>
                <span className="schedule-job-action-icon" aria-hidden="true">⏱</span>
                <span className="schedule-job-action-copy">
                  <strong>Request OT</strong>
                  <small>Sent to your manager for approval</small>
                </span>
                <span className="schedule-job-action-chevron" aria-hidden="true">›</span>
              </button>
              <button type="button" className="schedule-job-action-row" onClick={() => openExtraService(jobActionSchedule)}>
                <span className="schedule-job-action-icon" aria-hidden="true">📜</span>
                <span className="schedule-job-action-copy">
                  <strong>Extra service</strong>
                  <small>Add items to the latest service order</small>
                </span>
                <span className="schedule-job-action-chevron" aria-hidden="true">›</span>
              </button>

              <button
                type="button"
                className="schedule-job-final-action"
                onClick={() => void openCompanyServiceReport(jobActionSchedule)}
              >
                <span aria-hidden="true">✓</span> Complete job
              </button>
              <p className="schedule-job-final-help">
                Completing fills the report and closes your attendance.
              </p>
            </div>
          </section>
        </>
      )}

      {serviceCompletionLoading ? (
        <>
          <div className="schedule-sheet-bg" />
          <section className="schedule-sheet service-completion-sheet" role="dialog" aria-modal="true">
            <div className="schedule-sheet-handle" />
            <div className="schedule-sheet-header">
              <div className="schedule-sheet-title">Loading completion report…</div>
            </div>
            <div className="schedule-sheet-body service-completion-loading">
              <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          </section>
        </>
      ) : null}

      {serviceCompletionDetail ? (
        <ServiceCompletionSheet
          detail={serviceCompletionDetail}
          onClose={() => setServiceCompletionDetail(null)}
          onPrint={() => void printBusinessServiceReport(serviceCompletionDetail)}
          onSubmitted={async () => {
            toast.success('Service report submitted and job completed');
            setServiceCompletionDetail(null);
            setPageNumber(1);
            setSchedules([]);
            await fetchSchedules(false, 1);
          }}
        />
      ) : null}

      {jobPhotoSchedule && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setJobPhotoSchedule(null)} />
          <section
            className="schedule-sheet schedule-job-photo-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-job-photo-title"
          >
            <div className="schedule-sheet-handle" />
            <header className="schedule-sheet-header schedule-job-photo-header">
              <button
                type="button"
                className="schedule-sheet-back"
                onClick={() => {
                  setJobPhotoSchedule(null);
                  setJobActionSchedule(jobPhotoSchedule);
                }}
                aria-label="Back to job actions"
              >
                ‹
              </button>
              <div className="schedule-sheet-title" id="schedule-job-photo-title">Take photo</div>
              <button
                type="button"
                className="schedule-sheet-close"
                onClick={() => setJobPhotoSchedule(null)}
                aria-label="Close photo upload"
              >
                ×
              </button>
            </header>

            <div className="schedule-sheet-body schedule-job-photo-body">
              {servicePhotoLoading ? (
                <div className="schedule-job-photo-loading">Loading photos…</div>
              ) : (
                <div className="schedule-job-photo-columns">
                  {(['before', 'after'] as const).map((type) => {
                    const photos = serviceJobPhotos.filter((photo) => photo.type === type);
                    const inputRef = type === 'before' ? beforePhotoInputRef : afterPhotoInputRef;
                    return (
                      <div className="schedule-job-photo-column" key={type}>
                        <button
                          type="button"
                          className="schedule-job-photo-type"
                          onClick={() => inputRef.current?.click()}
                          disabled={servicePhotoUploading}
                        >
                          <span aria-hidden="true">📷</span>
                          <strong>{type === 'before' ? 'Before' : 'After'}</strong>
                        </button>
                        <input
                          ref={inputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          hidden
                          onChange={(event) => void handleServicePhotoAdd(event, type)}
                        />
                        <button
                          type="button"
                          className="schedule-job-photo-upload"
                          onClick={() => inputRef.current?.click()}
                          disabled={servicePhotoUploading}
                        >
                          {servicePhotoUploading ? 'Uploading…' : 'Upload photos'}
                        </button>
                        {photos.length ? (
                          <button
                            type="button"
                            className="schedule-job-photo-view"
                            onClick={() => setServicePhotoViewer({ type, index: 0 })}
                          >
                            View ({photos.length})
                          </button>
                        ) : (
                          <span className="schedule-job-photo-empty">No photo yet</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {otSchedule && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setOtSchedule(null)} />
          <section
            className="schedule-sheet schedule-job-ot-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-job-ot-title"
          >
            <div className="schedule-sheet-handle" />
            <header className="schedule-sheet-header">
              <div className="schedule-sheet-title" id="schedule-job-ot-title">Request OT</div>
              <button type="button" className="schedule-sheet-close" onClick={() => setOtSchedule(null)} aria-label="Close OT request">×</button>
            </header>
            <div className="schedule-sheet-body">
              <div className="schedule-job-ot-summary">
                <strong>{otSchedule.siteName || otSchedule.customerName || 'Service job'}</strong>
                <span>{otSchedule.documentNo || otSchedule.title || ''}</span>
              </div>
              <label className="schedule-job-ot-field">
                <span>OT hours *</span>
                <input
                  type="number"
                  min="1"
                  max="24"
                  inputMode="numeric"
                  value={otHours}
                  onChange={(event) => setOtHours(event.target.value)}
                />
              </label>
              <label className="schedule-job-ot-field">
                <span>Reason *</span>
                <textarea
                  rows={3}
                  value={otReason}
                  onChange={(event) => setOtReason(event.target.value)}
                  placeholder="Why is overtime required?"
                />
              </label>
              <button type="button" className="schedule-job-final-action" onClick={() => void submitOtRequest()} disabled={otSubmitting}>
                {otSubmitting ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </section>
        </>
      )}

      {extraServiceSchedule && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setExtraServiceSchedule(null)} />
          <section
            className="schedule-sheet schedule-extra-service-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-extra-service-title"
          >
            <div className="schedule-sheet-handle" />
            <header className="schedule-sheet-header">
              <div className="schedule-sheet-title" id="schedule-extra-service-title">Add extra service</div>
              <button
                type="button"
                className="schedule-sheet-close"
                onClick={() => setExtraServiceSchedule(null)}
                aria-label="Close extra service"
              >
                ×
              </button>
            </header>
            <div className="schedule-sheet-body schedule-extra-service-body">
              {extraServiceLoading ? (
                <div className="schedule-extra-service-loading">Loading service catalog…</div>
              ) : extraServiceDetail ? (
                <>
                  <div className="schedule-extra-service-summary">
                    <div>
                      <strong>{extraServiceDetail.customerName || extraServiceSchedule.siteName || 'Service job'}</strong>
                      <span>{extraServiceDetail.documentNo} · {extraServiceDetail.activeLineCount} item{extraServiceDetail.activeLineCount === 1 ? '' : 's'}</span>
                    </div>
                    <div className="schedule-extra-service-current-total">
                      <span>Current total</span>
                      <strong>{money(extraServiceDetail.currentTotal)}</strong>
                    </div>
                  </div>

                  {!extraServiceDetail.canEdit ? (
                    <div className="schedule-extra-service-locked">{extraServiceDetail.editMessage}</div>
                  ) : (
                    <>
                      <label className="schedule-job-ot-field schedule-extra-service-search">
                        <span>Service item *</span>
                        <input
                          type="search"
                          role="combobox"
                          aria-expanded="true"
                          aria-controls="schedule-extra-service-options"
                          aria-autocomplete="list"
                          value={extraServiceSearch}
                          onChange={(event) => setExtraServiceSearch(event.target.value)}
                          placeholder="Search catalog item or code"
                          autoComplete="off"
                        />
                      </label>

                      <div className="schedule-extra-service-selection-hint">
                        Tick one or more services, then set the quantity for each selected item.
                      </div>
                      <div className="schedule-extra-service-options" id="schedule-extra-service-options" role="group" aria-label="Available extra services">
                        {filteredExtraServiceItems.length ? filteredExtraServiceItems.slice(0, 30).map((item) => (
                          <div
                            key={item.id}
                            className={`schedule-extra-service-option ${Object.prototype.hasOwnProperty.call(extraServiceSelections, item.id) ? 'selected' : ''}`}
                          >
                            <label className="schedule-extra-service-checkbox" htmlFor={`extra-service-${item.id}`}>
                              <input
                                id={`extra-service-${item.id}`}
                                type="checkbox"
                                checked={Object.prototype.hasOwnProperty.call(extraServiceSelections, item.id)}
                                onChange={(event) => setExtraServiceSelections((current) => {
                                  if (event.target.checked) return { ...current, [item.id]: '1' };
                                  const next = { ...current };
                                  delete next[item.id];
                                  return next;
                                })}
                              />
                              <span aria-hidden="true">✓</span>
                            </label>
                            <label className="schedule-extra-service-option-copy" htmlFor={`extra-service-${item.id}`}>
                              <strong>{item.name}</strong>
                              <small>{[item.code, item.description].filter(Boolean).join(' · ')}</small>
                            </label>
                            <span className="schedule-extra-service-option-side">
                              <strong className="schedule-extra-service-option-price">{money(item.price)}</strong>
                              {Object.prototype.hasOwnProperty.call(extraServiceSelections, item.id) ? (
                                <label className="schedule-extra-service-inline-qty">
                                  <span>Qty</span>
                                  <input
                                    type="number"
                                    min="0.01"
                                    max="9999"
                                    step="0.01"
                                    inputMode="decimal"
                                    value={extraServiceSelections[item.id]}
                                    onChange={(event) => setExtraServiceSelections((current) => ({
                                      ...current,
                                      [item.id]: event.target.value,
                                    }))}
                                  />
                                </label>
                              ) : null}
                            </span>
                          </div>
                        )) : (
                          <div className="schedule-extra-service-empty">No catalog service item found.</div>
                        )}
                      </div>

                      <div className="schedule-extra-service-pricing schedule-extra-service-selection-summary" aria-live="polite">
                        <div><span>Selected</span><strong>{selectedExtraServiceItems.length} service{selectedExtraServiceItems.length === 1 ? '' : 's'}</strong></div>
                        <div><span>Additional total</span><strong>{money(extraServiceSelectionTotal)}</strong></div>
                        <div className="grand-total"><span>Updated Service Order</span><strong>{money(extraServiceDetail.currentTotal + extraServiceSelectionTotal)}</strong></div>
                      </div>

                      <button
                        type="button"
                        className="schedule-job-final-action"
                        onClick={() => void submitExtraService()}
                        disabled={!selectedExtraServiceItems.length || extraServiceSubmitting}
                      >
                        {extraServiceSubmitting
                          ? 'Adding…'
                          : `Add ${selectedExtraServiceItems.length || ''} service${selectedExtraServiceItems.length === 1 ? '' : 's'} to order`}
                      </button>
                    </>
                  )}
                </>
              ) : null}
            </div>
          </section>
        </>
      )}

      {/* Delivery Order completion and recipient confirmation */}
      {showDeliveryCompletion && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setShowDeliveryCompletion(false)} />
          <div className="schedule-sheet delivery-completion-sheet" role="dialog" aria-modal="true" aria-labelledby="delivery-completion-title">
            <div className="schedule-sheet-handle" />
            <div className="schedule-sheet-header">
              <div className="schedule-sheet-title" id="delivery-completion-title">
                {deliveryCompletionStage === 'edit'
                  ? `Completion Report: ${deliveryCompletionDraft?.documentNo || 'Delivery Order'}`
                  : 'Customer Confirmation'}
              </div>
              <button type="button" className="schedule-sheet-close" onClick={() => setShowDeliveryCompletion(false)} aria-label="Close">×</button>
            </div>

            <div className="schedule-sheet-body delivery-completion-body">
              {deliveryCompletionLoading ? (
                <div className="delivery-completion-loading">Loading delivery items…</div>
              ) : deliveryCompletionDraft && deliveryCompletionStage === 'edit' ? (
                <>
                  <div className="delivery-completion-summary">
                    <strong>{deliveryCompletionDraft.customerName} — {deliveryCompletionDraft.lines.length} item{deliveryCompletionDraft.lines.length === 1 ? '' : 's'}</strong>
                    <span>{deliveryCompletionDraft.documentNo}</span>
                  </div>

                  <div className="delivery-completion-label">Items delivered <span>· confirm receipt</span></div>
                  <div className="delivery-completion-items">
                    {deliveryCompletionDraft.lines.map((line, lineIndex) => (
                      <div className="delivery-completion-item" key={line.id}>
                        <input
                          type="checkbox"
                          checked={line.confirmed}
                          onChange={(event) => setDeliveryCompletionDraft((prev) => {
                            if (!prev) return prev;
                            const lines = [...prev.lines];
                            lines[lineIndex] = { ...line, confirmed: event.target.checked };
                            return { ...prev, lines };
                          })}
                        />
                        <div className="delivery-completion-item-copy">
                          <strong>{line.name}</strong>
                          <span>Ordered: {line.orderedQuantity} unit</span>
                        </div>
                        <label className="delivery-completion-qty">
                          <span>Recv</span>
                          <input
                            type="number"
                            min="0"
                            max={line.orderedQuantity}
                            step="any"
                            disabled={!line.confirmed}
                            value={line.confirmed ? line.receivedQuantity : 0}
                            onChange={(event) => setDeliveryCompletionDraft((prev) => {
                              if (!prev) return prev;
                              const lines = [...prev.lines];
                              lines[lineIndex] = {
                                ...line,
                                receivedQuantity: Number(event.target.value),
                              };
                              return { ...prev, lines };
                            })}
                          />
                        </label>
                      </div>
                    ))}
                  </div>

                  <label className="delivery-completion-field">
                    <span>Received by *</span>
                    <input
                      value={deliveryCompletionDraft.receivedBy}
                      onChange={(event) => setDeliveryCompletionDraft((prev) => prev ? ({ ...prev, receivedBy: event.target.value }) : prev)}
                    />
                  </label>

                  <label className="delivery-completion-field">
                    <span>Delivery notes</span>
                    <textarea
                      rows={3}
                      value={deliveryCompletionDraft.notes}
                      onChange={(event) => setDeliveryCompletionDraft((prev) => prev ? ({ ...prev, notes: event.target.value }) : prev)}
                    />
                  </label>

                  <div className="delivery-completion-time">
                    <div><span>Start</span><strong>{deliveryCompletionDraft.checkedInDateTime ? new Date(deliveryCompletionDraft.checkedInDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : 'now'}</strong></div>
                    <div><span>End</span><strong>on submit</strong></div>
                    <small>taken from your punches — no typing</small>
                  </div>

                  <div className="delivery-completion-label">Proof of delivery * <span>· upload photo(s) of goods / signed DO ({deliveryCompletionDraft.photos.length})</span></div>
                  <input
                    ref={deliveryPhotoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleDeliveryPhotoAdd}
                  />
                  {deliveryCompletionDraft.photos.length ? (
                    <div className="delivery-completion-photos">
                      {deliveryCompletionDraft.photos.map((photo, photoIndex) => (
                        <div className="delivery-completion-photo" key={`${photo.dataUrl.slice(0, 24)}-${photoIndex}`}>
                          <div>
                            <img src={photo.dataUrl} alt={`Delivery proof ${photoIndex + 1}`} />
                            <button
                              type="button"
                              onClick={() => setDeliveryCompletionDraft((prev) => prev ? ({
                                ...prev,
                                photos: prev.photos.filter((_, index) => index !== photoIndex),
                              }) : prev)}
                              aria-label="Remove photo"
                            >×</button>
                          </div>
                          <input
                            placeholder="caption"
                            value={photo.caption}
                            onChange={(event) => setDeliveryCompletionDraft((prev) => {
                              if (!prev) return prev;
                              const photos = [...prev.photos];
                              photos[photoIndex] = { ...photo, caption: event.target.value };
                              return { ...prev, photos };
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <button type="button" className="delivery-completion-upload" onClick={() => deliveryPhotoInputRef.current?.click()}>
                    📷 <strong>Add photos</strong> <span>· camera or gallery, many at once</span>
                  </button>
                  <button type="button" className="delivery-completion-primary" onClick={reviewDeliveryCompletion}>
                    Review with recipient ›
                  </button>
                </>
              ) : deliveryCompletionDraft ? (
                <>
                  <div className="delivery-confirm-handoff">📱 Please hand the device to the recipient</div>
                  <button type="button" className="delivery-confirm-back" onClick={() => setDeliveryCompletionStage('edit')}>‹ Back to edit</button>

                  <div className="delivery-completion-summary">
                    <strong>{deliveryCompletionDraft.customerName}</strong>
                    <span>{deliveryCompletionDraft.lines.length} item · {deliveryCompletionDraft.documentNo}{deliveryCompletionDraft.deliveryDate ? ` · ${new Date(deliveryCompletionDraft.deliveryDate).toLocaleDateString('en-MY', { weekday: 'short', day: '2-digit', month: 'short' })}` : ''}</span>
                  </div>

                  <div className="delivery-confirm-block">
                    <span>Items received</span>
                    {deliveryCompletionDraft.lines.map((line) => (
                      <div className="delivery-confirm-item" key={line.id}>
                        <strong>{line.name}</strong>
                        <b>{line.confirmed ? line.receivedQuantity : 0}/{line.orderedQuantity} unit</b>
                      </div>
                    ))}
                  </div>
                  <div className="delivery-confirm-block"><span>Received by</span><strong>{deliveryCompletionDraft.receivedBy}</strong></div>
                  {deliveryCompletionDraft.notes ? <div className="delivery-confirm-block"><span>Delivery notes</span><strong>{deliveryCompletionDraft.notes}</strong></div> : null}
                  <div className="delivery-confirm-photo-row">
                    {deliveryCompletionDraft.photos.map((photo, index) => <img src={photo.dataUrl} alt={`Delivery proof ${index + 1}`} key={index} />)}
                  </div>
                  <div className="delivery-confirm-block">
                    <span>Time</span>
                    <strong>Started {deliveryCompletionDraft.checkedInDateTime ? new Date(deliveryCompletionDraft.checkedInDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : 'now'} · ends on submit</strong>
                  </div>
                  <div className="delivery-confirm-statement">I confirm receipt of the items listed above.</div>

                  <div className="delivery-completion-label">Recipient signature</div>
                  <div className="delivery-signature-wrap">
                    <SignatureCanvas
                      ref={deliverySignatureRef}
                      penColor="#111827"
                      canvasProps={{ className: 'delivery-signature-canvas' }}
                    />
                    <span>Sign here</span>
                  </div>
                  <button type="button" className="delivery-signature-clear" onClick={() => deliverySignatureRef.current?.clear()}>Clear</button>

                  <button
                    type="button"
                    className="delivery-completion-primary"
                    disabled={deliveryCompletionSubmitting}
                    onClick={() => void submitDeliveryCompletion(false)}
                  >
                    {deliveryCompletionSubmitting ? 'Submitting…' : 'Confirm & submit'}
                  </button>
                  <button
                    type="button"
                    className="delivery-confirm-skip"
                    disabled={deliveryCompletionSubmitting}
                    onClick={() => void submitDeliveryCompletion(true)}
                  >
                    Skip — POD photos stand in
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* Site Visit Report sheet */}
      {showSiteVisitReport && siteVisitReportSchedule && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setShowSiteVisitReport(false)} />
          <div className="schedule-sheet" role="dialog" aria-modal="true" aria-labelledby="site-visit-report-title">
            <div className="schedule-sheet-handle" />
            <div className="schedule-sheet-header">
              <div className="schedule-sheet-title" id="site-visit-report-title">Site Visit Report</div>
              <button type="button" className="schedule-sheet-close" onClick={() => setShowSiteVisitReport(false)} aria-label="Close">×</button>
            </div>
            <div className="schedule-sheet-body">
              <div className="rounded-xl bg-gray-50 px-3 py-3 mb-4 text-xs">
                <div className="font-extrabold text-gray-900 mb-1">
                  {siteVisitReportSchedule.siteName || siteVisitReportSchedule.title || 'Site visit'}
                </div>
                <div className="text-gray-500">
                  {formatBmScheduleDateTime(siteVisitReportSchedule.scheduleDate || siteVisitReportSchedule.date, siteVisitReportSchedule.scheduledTime || siteVisitReportSchedule.time)}
                </div>
              </div>

              <div className="schedule-fg">
                <div className="schedule-inp-label">Findings *</div>
                <textarea
                  className="schedule-inp"
                  rows={3}
                  placeholder="Site condition, scope, recommendation..."
                  value={siteVisitDraft.findings}
                  onChange={(e) => setSiteVisitDraft((prev) => ({ ...prev, findings: e.target.value }))}
                />
              </div>

              <div className="schedule-fg">
                <div className="schedule-inp-label">Equipment / Site Condition</div>
                <input
                  className="schedule-inp"
                  placeholder="e.g. 18 aircon units · grease buildup in kitchen"
                  value={siteVisitDraft.equipment}
                  onChange={(e) => setSiteVisitDraft((prev) => ({ ...prev, equipment: e.target.value }))}
                />
              </div>

              <div className="schedule-fg">
                <div className="schedule-inp-label">Scope / Requirements</div>
                {siteVisitDraft.requirements.map((req, idx) => (
                  <input
                    key={`req-${idx}`}
                    className="schedule-inp mb-2"
                    placeholder={`Scope item ${idx + 1}`}
                    value={req}
                    onChange={(e) => {
                      const next = [...siteVisitDraft.requirements];
                      next[idx] = e.target.value;
                      setSiteVisitDraft((prev) => ({ ...prev, requirements: next }));
                    }}
                  />
                ))}
                <button
                  type="button"
                  className="schedule-scope-add"
                  onClick={() => setSiteVisitDraft((prev) => ({ ...prev, requirements: [...prev.requirements, ''] }))}
                >
                  + Add scope item
                </button>
              </div>

              <div className="schedule-fg">
                <div className="schedule-inp-label">Recommendation</div>
                <textarea
                  className="schedule-inp"
                  rows={2}
                  placeholder="Recommended package / frequency / next steps for the quote..."
                  value={siteVisitDraft.recommendation}
                  onChange={(e) => setSiteVisitDraft((prev) => ({ ...prev, recommendation: e.target.value }))}
                />
              </div>

              <div className="schedule-fg">
                <div className="schedule-inp-label">Survey Photos ({siteVisitDraft.photos.length})</div>
                <input
                  ref={siteVisitPhotoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleSiteVisitPhotoAdd}
                />
                {siteVisitDraft.photos.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {siteVisitDraft.photos.map((src, i) => (
                      <img key={`${src.slice(0, 24)}-${i}`} src={src} alt={`Survey ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border" />
                    ))}
                  </div>
                ) : null}
                <button type="button" className="schedule-scope-add" onClick={() => siteVisitPhotoInputRef.current?.click()}>
                  📷 Add photos · camera or gallery
                </button>
              </div>

              <button
                type="button"
                className="schedule-btn-red"
                onClick={handleSubmitSiteVisitReport}
              >
                Save site visit report
              </button>
            </div>
          </div>
        </>
      )}

      {/* Business Management Job/Trip Check-in Modal */}
      {showBmCheckInModal && bmCheckInData && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4">
          <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {bmCheckInData.kind === 'job' ? 'Job Check In' : 'Trip Check In'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowBmCheckInModal(false);
                  setBmCheckInData(null);
                }}
                className="w-8 h-8 rounded-full border border-gray-200 text-gray-500 flex items-center justify-center"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="p-4">
              <div className="rounded-xl bg-gray-50 px-4 py-3 mb-4">
                <div className="font-bold text-gray-900">{bmCheckInData.workerDisplayName}</div>
                <div className="text-sm text-gray-500">{bmCheckInData.siteName}</div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image <span className="text-primary">*</span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg,image/heic"
                onChange={handleBmImageChange}
                ref={bmFileInputRef}
                className="hidden"
              />
              <div
                className={`relative border-2 ${bmCheckInData.image ? 'border-primary' : 'border-dashed border-gray-300'} rounded-xl overflow-hidden mb-4`}
                onClick={() => bmFileInputRef.current?.click()}
              >
                {bmCheckInData.image ? (
                  <div className="relative">
                    <img
                      src={URL.createObjectURL(bmCheckInData.image)}
                      alt="Preview"
                      className="w-full h-44 object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBmCheckInData((prev) => prev ? ({ ...prev, image: null }) : prev);
                      }}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow text-primary"
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                    <div className="text-3xl mb-2" aria-hidden="true">📷</div>
                    <p className="text-gray-700 font-medium">Tap to take a photo</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="rounded-xl bg-gray-50 px-3 py-3">
                  <div className="text-xs text-gray-500 mb-1">Time</div>
                  <div className="font-bold text-gray-900">{formatLiveClock(bmModalClock)}</div>
                </div>
                <div className="rounded-xl bg-gray-50 px-3 py-3">
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="font-bold text-gray-900 text-sm">
                    {bmCheckInData.latitude && bmCheckInData.longitude ? 'Captured' : 'Locating...'}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSubmitBmCheckIn}
                disabled={isSubmitting || !bmCheckInData.image || !bmCheckInData.latitude || !bmCheckInData.longitude}
                className={`w-full py-3 rounded-xl text-white font-semibold mb-3 ${
                  isSubmitting || !bmCheckInData.image || !bmCheckInData.latitude || !bmCheckInData.longitude
                    ? 'bg-gray-400'
                    : 'bg-primary'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Check-in'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowBmCheckInModal(false);
                  setBmCheckInData(null);
                }}
                className="w-full py-2 text-center text-gray-700 font-medium"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check-in Modal */}
      {showCheckInModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn pb-safe">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp">
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Check In</h2>
              <button 
                onClick={() => setShowCheckInModal(false)}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/heic"
                  onChange={handleImageChange}
                  ref={fileInputRef}
                  className="hidden"
                />
                
                <div 
                  className={`relative border-2 ${checkInData.image ? 'border-primary' : 'border-dashed border-gray-300'} rounded-lg overflow-hidden`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {checkInData.image ? (
                    <div className="relative">
                      <img
                        src={URL.createObjectURL(checkInData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white px-2 py-1 text-xs">
                        {checkInData.image.name} ({Math.round(checkInData.image.size / 1024)} KB)
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCheckInData(prev => ({ ...prev, image: null }));
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md text-red-500"
                        aria-label="Remove image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                      <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 font-medium">Tap to take a photo</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center mb-5">
                <div className="mr-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-700">Location Status</div>
                  {checkInData.latitude && checkInData.longitude ? (
                    <div className="text-sm text-green-600">
                      ✓ Location captured
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-600">
                      Capturing location...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handleSubmitCheckIn}
                  disabled={isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude}
                  className={`w-full py-3 rounded-md text-white font-medium text-center ${
                    isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude
                      ? 'bg-gray-400'
                      : 'bg-primary'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Check-in'}
                </button>
                
                <button
                  onClick={() => setShowCheckInModal(false)}
                  className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showListFilter && (
        <>
          <div className="schedule-sheet-bg schedule-list-filter-backdrop" onClick={() => setShowListFilter(false)} />
          <section
            className="schedule-sheet schedule-list-filter-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-list-filter-title"
          >
            <div className="schedule-sheet-handle" />
            <header className="schedule-sheet-header">
              <h2 id="schedule-list-filter-title" className="schedule-sheet-title">Filter</h2>
              <button
                type="button"
                className="schedule-sheet-close"
                onClick={() => setShowListFilter(false)}
                aria-label="Close filters"
              >
                ×
              </button>
            </header>

            <div className="schedule-list-filter-body">
              <fieldset className="schedule-filter-group">
                <legend>Show</legend>
                <div className="schedule-filter-chips">
                  {([
                    ['all', 'All'],
                    ['jobs', 'Jobs'],
                    ['interview', 'Interview'],
                    ['new-joiner', 'New Joiner'],
                    ['transport', 'Transport'],
                    ['compliance', 'Compliance'],
                    ['notice', 'Notice'],
                    ['company-event', 'Company Event'],
                    ['holiday', 'Holiday'],
                    ['leave', 'Leave'],
                  ] as Array<[ScheduleShowFilter, string]>).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={draftListFilters.show === value ? 'active' : ''}
                      onClick={() => setDraftListFilters((current) => ({ ...current, show: value }))}
                      aria-pressed={draftListFilters.show === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset className="schedule-filter-group">
                <legend>Date</legend>
                <div className="schedule-filter-chips">
                  {([
                    ['all', 'All dates'],
                    ['today', 'Today'],
                    ['next-7', 'Next 7 days'],
                    ['upcoming', 'Upcoming'],
                  ] as Array<[ScheduleDateFilter, string]>).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={draftListFilters.date === value ? 'active' : ''}
                      onClick={() => setDraftListFilters((current) => ({ ...current, date: value }))}
                      aria-pressed={draftListFilters.date === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="schedule-filter-date-row">
                  <label>
                    <span>From</span>
                    <button
                      type="button"
                      className="schedule-filter-date-trigger"
                      onClick={() => {
                        setDraftListFilters((current) => ({ ...current, date: 'custom' }));
                        setFilterDatePickerTarget('from');
                      }}
                      aria-haspopup="dialog"
                    >
                      <span className={draftListFilters.fromDate ? '' : 'placeholder'}>
                        {draftListFilters.fromDate
                          ? format(parseApiDateOnly(draftListFilters.fromDate) || new Date(), 'dd/MM/yyyy')
                          : 'dd/mm/yyyy'}
                      </span>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </button>
                  </label>
                  <label>
                    <span>To</span>
                    <button
                      type="button"
                      className="schedule-filter-date-trigger"
                      onClick={() => {
                        setDraftListFilters((current) => ({ ...current, date: 'custom' }));
                        setFilterDatePickerTarget('to');
                      }}
                      aria-haspopup="dialog"
                    >
                      <span className={draftListFilters.toDate ? '' : 'placeholder'}>
                        {draftListFilters.toDate
                          ? format(parseApiDateOnly(draftListFilters.toDate) || new Date(), 'dd/MM/yyyy')
                          : 'dd/mm/yyyy'}
                      </span>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </button>
                  </label>
                </div>
              </fieldset>

              <fieldset className="schedule-filter-group">
                <legend>Job type</legend>
                <div className="schedule-filter-chips">
                  {([
                    ['all', 'All types'],
                    ['delivery', 'Delivery'],
                    ['service', 'Service'],
                    ['site-visit', 'Site Visit'],
                    ['touch-up', 'Touch-up'],
                  ] as Array<[ScheduleJobTypeFilter, string]>).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={draftListFilters.jobType === value ? 'active' : ''}
                      onClick={() => setDraftListFilters((current) => ({ ...current, jobType: value }))}
                      aria-pressed={draftListFilters.jobType === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>

              <label className="schedule-filter-worker">
                <span>Worker</span>
                <select
                  value={draftListFilters.worker}
                  onChange={(event) => setDraftListFilters((current) => ({ ...current, worker: event.target.value }))}
                >
                  <option value="">Any worker</option>
                  {listWorkerOptions.map((worker) => <option key={worker} value={worker}>{worker}</option>)}
                </select>
              </label>
            </div>

            <footer className="schedule-list-filter-footer">
              <button type="button" className="schedule-filter-clear" onClick={clearListFilters}>Clear all</button>
              <button type="button" className="schedule-filter-apply" onClick={applyListFilters}>Show</button>
            </footer>
          </section>
        </>
      )}

      {scheduleActivity && (
        <>
          <div className="schedule-sheet-bg" onClick={() => setScheduleActivity(null)} />
          <section className="schedule-sheet schedule-activity-sheet" role="dialog" aria-modal="true" aria-labelledby="schedule-activity-title">
            <div className="schedule-sheet-handle" />
            <header className="schedule-sheet-header">
              <div>
                <div className="schedule-sheet-title" id="schedule-activity-title">Activity log</div>
                <div className="schedule-activity-document">{scheduleActivity.title}</div>
              </div>
              <button type="button" className="schedule-sheet-close" onClick={() => setScheduleActivity(null)} aria-label="Close">×</button>
            </header>
            <div className="schedule-sheet-body schedule-activity-body">
              {scheduleActivity.items.length ? (
                <div className="schedule-activity-timeline">
                  {scheduleActivity.items.map((item, index) => (
                    <article className={`schedule-activity-item ${item.isCurrent ? 'current' : ''}`} key={`${item.eventTime}-${item.action}-${index}`}>
                      <i aria-hidden="true" />
                      <div className="schedule-activity-heading">
                        <strong>{item.action}</strong>
                        {item.isCurrent ? <span>Current</span> : null}
                      </div>
                      <div className="schedule-activity-meta">
                        <span>◷ {formatActivityDateTime(item.eventTime)}</span>
                        {(item.by || item.role) ? <span>♟ {item.by || item.role}{item.by && item.role ? ` · ${item.role}` : ''}</span> : null}
                      </div>
                      {item.detail ? <div className="schedule-activity-detail">{item.detail}</div> : null}
                    </article>
                  ))}
                </div>
              ) : (
                <div className="schedule-activity-empty">No activity has been recorded for this schedule.</div>
              )}
            </div>
          </section>
        </>
      )}

      <MobileDatePicker
        isOpen={filterDatePickerTarget !== null}
        onClose={() => setFilterDatePickerTarget(null)}
        selectedDate={filterDatePickerTarget
          ? parseApiDateOnly(draftListFilters[filterDatePickerTarget === 'from' ? 'fromDate' : 'toDate'])
          : null}
        initialDate={filterDatePickerTarget
          ? parseApiDateOnly(draftListFilters[filterDatePickerTarget === 'from' ? 'fromDate' : 'toDate']) || new Date()
          : new Date()}
        onSelectDate={(date) => {
          if (!filterDatePickerTarget || !date) return;
          const field = filterDatePickerTarget === 'from' ? 'fromDate' : 'toDate';
          setDraftListFilters((current) => ({
            ...current,
            date: 'custom',
            [field]: formatDateForApi(date),
          }));
          setFilterDatePickerTarget(null);
        }}
      />

      {servicePhotoViewer && (() => {
        const photos = serviceJobPhotos.filter((photo) => photo.type === servicePhotoViewer.type);
        const activeIndex = Math.min(servicePhotoViewer.index, Math.max(photos.length - 1, 0));
        const activePhoto = photos[activeIndex];
        const label = servicePhotoViewer.type === 'before' ? 'Before photos' : 'After photos';
        const moveTo = (index: number) => {
          if (!photos.length) return;
          setServicePhotoViewer((current) => current ? {
            ...current,
            index: Math.max(0, Math.min(index, photos.length - 1)),
          } : null);
        };

        return activePhoto ? (
          <section className="schedule-photo-viewer" role="dialog" aria-modal="true" aria-label={label}>
            <header className="schedule-photo-viewer-header">
              <button type="button" onClick={() => setServicePhotoViewer(null)} aria-label="Close photo gallery">×</button>
              <div>
                <strong>{label}</strong>
                <span>{activeIndex + 1} of {photos.length}</span>
              </div>
              <span aria-hidden="true" />
            </header>

            <div
              className="schedule-photo-viewer-stage"
              onTouchStart={(event) => { servicePhotoSwipeStartX.current = event.touches[0]?.clientX ?? null; }}
              onTouchEnd={(event) => {
                if (servicePhotoSwipeStartX.current == null) return;
                const distance = event.changedTouches[0].clientX - servicePhotoSwipeStartX.current;
                servicePhotoSwipeStartX.current = null;
                if (Math.abs(distance) < 45) return;
                moveTo(activeIndex + (distance < 0 ? 1 : -1));
              }}
            >
              <img src={activePhoto.dataUrl} alt={`${label}, ${activeIndex + 1} of ${photos.length}`} />
              {activeIndex > 0 ? (
                <button type="button" className="schedule-photo-viewer-nav previous" onClick={() => moveTo(activeIndex - 1)} aria-label="Previous photo">‹</button>
              ) : null}
              {activeIndex < photos.length - 1 ? (
                <button type="button" className="schedule-photo-viewer-nav next" onClick={() => moveTo(activeIndex + 1)} aria-label="Next photo">›</button>
              ) : null}
            </div>

            <footer className="schedule-photo-viewer-footer">
              {activePhoto.caption ? <p>{activePhoto.caption}</p> : null}
              {photos.length > 1 ? (
                <div className="schedule-photo-viewer-thumbnails" aria-label="Choose photo">
                  {photos.map((photo, index) => (
                    <button
                      type="button"
                      className={index === activeIndex ? 'active' : ''}
                      onClick={() => moveTo(index)}
                      aria-label={`View photo ${index + 1}`}
                      aria-current={index === activeIndex ? 'true' : undefined}
                      key={photo.id}
                    >
                      <img src={photo.dataUrl} alt="" />
                    </button>
                  ))}
                </div>
              ) : null}
            </footer>
          </section>
        ) : null;
      })()}

      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
};

export default Schedule;
